import { JLI_LOGO_ZPL_HEX, JLI_LOGO_ZPL_BYTES_PER_ROW, JLI_LOGO_ZPL_TOTAL_BYTES, JLI_LOGO_ZPL_WIDTH, JLI_LOGO_ZPL_HEIGHT } from "./jliLogoZpl";
import { readableTextOn } from "./labelContrast";

const PRINT_AGENT_URL = "http://localhost:9120";
// Hosted within the FE — single-click installer. Same-origin copy: the
// fallback when the staging host (below) is unreachable.
export const PRINT_AGENT_DOWNLOAD_URL = "/PrintAgent-Setup.exe";
// The staging FE always carries the NEWEST agent build — production FE
// deploys lag behind for the agent files — so downloads prefer it.
const PRINT_AGENT_DOWNLOAD_PRIMARY = "https://jli-portal-fe-staging.onrender.com/PrintAgent-Setup.exe";

/** Resolve the best installer download URL: the staging FE (always the
 *  newest build) when reachable, else this origin's own copy. The probe is
 *  a no-cors HEAD — we only need "did the host answer", not the body. */
export async function resolvePrintAgentDownloadUrl(): Promise<string> {
    if (window.location.origin === new URL(PRINT_AGENT_DOWNLOAD_PRIMARY).origin) {
        return PRINT_AGENT_DOWNLOAD_URL; // already on staging — same file
    }
    try {
        await fetch(PRINT_AGENT_DOWNLOAD_PRIMARY, {
            method: "HEAD",
            mode: "no-cors",
            signal: AbortSignal.timeout(2500),
        });
        return PRINT_AGENT_DOWNLOAD_PRIMARY;
    } catch {
        return PRINT_AGENT_DOWNLOAD_URL;
    }
}
// Bump this when the agent gains required new functionality. The FE will
// prompt users whose local agent reports a lower version to update.
export const EXPECTED_AGENT_VERSION = "1.6.6";

// Status probe timeout. Generous on purpose: when no agent is installed the
// connection refuses instantly anyway, and pre-1.4.0 agents lazily launch
// Chrome inside /status — a 1s timeout misread that warm-up as "not
// installed" and hid the Local printer option.
const STATUS_TIMEOUT_MS = 4000;

/** Check if the local print agent is running */
export async function isPrintAgentAvailable(): Promise<boolean> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/status`, { signal: AbortSignal.timeout(STATUS_TIMEOUT_MS) });
        const data = await res.json();
        return data.status === "ok";
    } catch {
        return false;
    }
}

/** Get the local print agent's status including version. Returns null if
 *  the agent isn't reachable. Use the version field to detect outdated
 *  installs that need updating. */
export async function getPrintAgentStatus(): Promise<{ status: string; port: number; version?: string; hostname?: string } | null> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/status`, { signal: AbortSignal.timeout(STATUS_TIMEOUT_MS) });
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

/** List the local agent's printers (Windows printer names) plus its current
 *  default target. Empty list if the agent is unreachable. */
export async function getLocalPrinters(): Promise<{ printers: string[]; target: string | null }> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/printers`, { signal: AbortSignal.timeout(STATUS_TIMEOUT_MS) });
        if (!res.ok) return { printers: [], target: null };
        const data = await res.json();
        return {
            printers: Array.isArray(data.printers) ? data.printers : [],
            target: typeof data.target === "string" ? data.target : null,
        };
    } catch {
        return { printers: [], target: null };
    }
}

// The specific local printer the user picked in the printer-select modal.
// null = no explicit choice → the agent prints to the Windows default
// (pre-1.4.0 behaviour, and the fallback for agents that ignore `printer`).
const LOCAL_PRINTER_KEY = "local-printer-selection";

export function getSavedLocalPrinter(): string | null {
    try {
        return localStorage.getItem(LOCAL_PRINTER_KEY) || null;
    } catch { return null; }
}

export function saveLocalPrinter(name: string) {
    localStorage.setItem(LOCAL_PRINTER_KEY, name);
}

export function clearLocalPrinter() {
    localStorage.removeItem(LOCAL_PRINTER_KEY);
}

// Which print route the user explicitly chose in the printer-select modal.
// Without this, printer detection always preferred an online saved network
// printer — so picking "Local" only lasted until the next detection run,
// then the device silently switched back to the remote printer. The
// preference is persisted per device and wins whenever its route is usable.
export type PrintModePreference = "local" | "network";
const PRINT_MODE_PREF_KEY = "print-mode-preference";

export function getPrintModePreference(): PrintModePreference | null {
    try {
        const v = localStorage.getItem(PRINT_MODE_PREF_KEY);
        return v === "local" || v === "network" ? v : null;
    } catch { return null; }
}

export function savePrintModePreference(mode: PrintModePreference) {
    localStorage.setItem(PRINT_MODE_PREF_KEY, mode);
}

export function clearPrintModePreference() {
    localStorage.removeItem(PRINT_MODE_PREF_KEY);
}

/** Compare two semver-ish version strings (e.g. "1.2.3").
 *  Returns true if `current` is older than `expected`. */
export function isAgentOutdated(currentVersion: string | undefined, expectedVersion: string = EXPECTED_AGENT_VERSION): boolean {
    if (!currentVersion) return true; // No version reported = old agent
    const cur = currentVersion.split(".").map(n => parseInt(n, 10) || 0);
    const exp = expectedVersion.split(".").map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(cur.length, exp.length); i++) {
        const a = cur[i] || 0;
        const b = exp[i] || 0;
        if (a < b) return true;
        if (a > b) return false;
    }
    return false; // Equal versions
}

export interface SilentPrintBarcodeOpts {
    /** Render the wipe DISPOSE banner on the ZPL label (agent ≥ 1.3.0). */
    dispose?: boolean;
    /** HTML to print instead of the generic barcode label when the target
     *  printer isn't a Zebra and the agent falls back to the HTML/PDF path. */
    fallbackHtml?: string;
    /** Human-readable name rendered BIG on the ZPL label, with the barcode
     *  value small underneath (agent ≥ 1.4.0 — older agents ignore it and
     *  print the barcode-only layout). Used for dispatch box labels. */
    name?: string;
    /** Specific local printer to print on (Windows printer name). Omitted →
     *  the agent's default target. HTML fallback needs agent ≥ 1.4.0. */
    printer?: string;
    /** Small line under the barcode value on the standard layout (agent ≥
     *  1.6.3 — older agents ignore it and print barcode + value only).
     *  Used for the supplier on Manual Products labels. */
    footer?: string;
    /** Physical label stock size in mm for the ZPL path (agent ≥ 1.5.0 —
     *  older agents ignore it and assume 65×38). Box labels pass the
     *  warehouse's configured box-label / wipe size so the ZPL geometry
     *  matches the loaded stock. */
    labelWidthMm?: number;
    labelHeightMm?: number;
}

/** Fast barcode print — uses ZPL for Zebra printers, falls back to HTML/PDF.
 *  Returns { success, error? } */
export async function silentPrintBarcode(value: string, copies: number = 1, opts: SilentPrintBarcodeOpts = {}): Promise<{ success: boolean; error?: string }> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/print-barcode`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                value,
                copies,
                dispose: Boolean(opts.dispose),
                ...(opts.name ? { name: opts.name } : {}),
                ...(opts.footer ? { footer: opts.footer } : {}),
                ...(opts.printer ? { printer: opts.printer } : {}),
                ...(opts.labelWidthMm && opts.labelWidthMm > 0 ? { labelWidth: opts.labelWidthMm } : {}),
                ...(opts.labelHeightMm && opts.labelHeightMm > 0 ? { labelHeight: opts.labelHeightMm } : {}),
            }),
        });
        const data = await res.json();
        if (data.success) return { success: true };
        // If non-Zebra, fall back to HTML path
        if (data.error === "Use /print for non-Zebra printers") {
            return silentPrint(opts.fallbackHtml ?? buildBarcodeLabel(value), copies, opts.printer);
        }
        return { success: false, error: data.error };
    } catch {
        return { success: false, error: "Could not reach print agent" };
    }
}

/** Print a raw ZPL document verbatim on a local Zebra printer (agents
 *  ≥ 1.6.0 — used for carrier shipping labels, e.g. APC's 6"x4" ZPL).
 *  Older agents 404 the endpoint; both that and a non-Zebra printer
 *  come back as { success: false } so callers fall back to PDF.
 *  Returns { success, error? } */
export async function silentPrintZplRaw(zpl: string, copies: number = 1, printer?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/print-zpl`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ zpl, copies, ...(printer ? { printer } : {}) }),
        });
        if (res.status === 404) {
            return { success: false, error: "Print agent update required (raw ZPL needs v1.6.0+)" };
        }
        const data = await res.json();
        return { success: data.success === true, error: data.error };
    } catch {
        return { success: false, error: "Could not reach print agent" };
    }
}

/** Send HTML to the local print agent for silent printing. Pass `printer`
 *  (a Windows printer name) to target a specific local printer — agents
 *  ≥ 1.4.0 honour it; older agents ignore it and print to their default.
 *  Returns { success, error? } */
export async function silentPrint(html: string, copies: number = 1, printer?: string): Promise<{ success: boolean; error?: string }> {
    try {
        const res = await fetch(`${PRINT_AGENT_URL}/print`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ html, copies, ...(printer ? { printer } : {}) }),
        });
        const data = await res.json();
        return { success: data.success === true, error: data.error };
    } catch (err) {
        return { success: false, error: "Could not reach print agent" };
    }
}

// Inline SVG of the JLI logo so the label renders even when the printer's
// host has no internet / can't reach the FE bundle. fills are baked in
// (replacing the original class-based style block) to keep the SVG self-
// contained inside the label HTML.
const JLI_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 819.9 426.86" preserveAspectRatio="xMidYMid meet">
  <g fill="#1a1a1a">
    <path d="M5.7,222.36v-10.05h70.81v6.46c0,19.53,11.53,32.11,28.97,32.11s28.13-12.37,28.13-32.11V54.68h70.81v167.69c0,55.3-39.81,93.94-98.95,93.94S5.7,277.81,5.7,222.36Z"/>
    <path d="M413.44,245.47v64.38h-162.04V54.68h71.23v190.8h90.81Z"/>
    <path d="M444.96,54.68h71.23v255.17h-71.23V54.68Z"/>
  </g>
  <rect fill="#ec6f20" x="564.72" y="54.68" width="255.17" height="255.17"/>
</svg>`;

export interface PalletLabelOpts {
    warehouse: string;
    createdAt: string;     // ISO timestamp
    batchIndex: number;    // 1-based position in this print batch
    batchTotal: number;    // total labels in this batch
    dailySeq: number;      // running daily counter for the warehouse
    operator?: string;     // name of the user who printed the labels
    /** Inbound stock type chosen at print time (Goods In settings), e.g.
     *  "Envelopes". Printed big above the barcode so the label reads at a
     *  glance; omitted for warehouses with no types configured. */
    stockType?: string;
    /** Supplier chosen at print time (migration 196), e.g. "Amazon".
     *  Printed under the stock type; not part of the barcode. */
    supplier?: string;
}

const escapeHtmlText = (s: string) => s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Any value as escaped HTML text. Every data field a label template
 *  interpolates goes through this (or jsString below) — the document is
 *  written into a same-origin popup by browserPrint, so unescaped order
 *  numbers, names or barcodes would run as the Portal (security audit
 *  2026-10-19, M7). */
const esc = (v: unknown) => escapeHtmlText(String(v ?? ""));

/** A quoted JS string literal safe to inline inside a <script> block. */
const jsString = (v: unknown) =>
    JSON.stringify(String(v ?? ""))
        .replace(/<\/(script)/gi, "<\\/$1")
        .replace(/<!--/g, "<\\!--");

/** The one human-readable line the print agent's ZPL layout can show
 *  above an inbound barcode (`name` on /print-barcode). Stock type and
 *  supplier are joined so both reach a Zebra label; either alone is used
 *  as-is; neither → undefined (barcode-only layout, as before). */
export function inboundLabelName(stockType?: string | null, supplier?: string | null): string | undefined {
    const parts = [stockType, supplier]
        .map(s => (s ?? "").trim())
        .filter(Boolean);
    return parts.length ? parts.join(" · ") : undefined;
}

/** Build a 6"x4" pallet receiving label. Includes JLI logo, warehouse name,
 *  CODE128 barcode, batch position, and daily counter. Used as the HTML
 *  fallback for non-Zebra printers — Zebra prints a ZPL barcode-only label. */
export function buildPalletLabel(barcodeValue: string, opts: PalletLabelOpts): string {
    const created = new Date(opts.createdAt);
    const dd = String(created.getDate()).padStart(2, "0");
    const mm = String(created.getMonth() + 1).padStart(2, "0");
    const yyyy = created.getFullYear();
    const hh = String(created.getHours()).padStart(2, "0");
    const min = String(created.getMinutes()).padStart(2, "0");
    const receivedStr = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    const dailySeqStr = String(opts.dailySeq).padStart(3, "0");
    // Escape the operator name so a user with " or < in their name
    // can't break the label HTML. Plain entity escaping is enough —
    // only injected as text content, never as an attribute.
    const operatorEsc = escapeHtmlText(opts.operator ?? "");
    const operatorLine = operatorEsc ? `<div class="received-by">By ${operatorEsc}</div>` : "";
    const stockTypeEsc = escapeHtmlText((opts.stockType ?? "").trim());
    const stockTypeLine = stockTypeEsc ? `<div class="stock-type">${stockTypeEsc}</div>` : "";
    const supplierEsc = escapeHtmlText((opts.supplier ?? "").trim());
    const supplierLine = supplierEsc ? `<div class="supplier">${supplierEsc}</div>` : "";

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 6in 4in; margin: 0; }
body {
    width: 6in;
    height: 4in;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    display: flex;
    flex-direction: column;
    padding: 0.18in 0.22in;
}
.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 0.7in;
    flex-shrink: 0;
}
.header svg {
    height: 0.6in;
    width: auto;
}
.warehouse {
    font-size: 36px;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.stock-type {
    flex-shrink: 0;
    text-align: center;
    font-size: 44px;
    font-weight: 900;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.05;
    margin-top: 0.04in;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.supplier {
    flex-shrink: 0;
    text-align: center;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.1;
    margin-top: 0.02in;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.barcode-block {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.05in 0;
}
.barcode-block svg {
    width: 5in !important;
    height: 1.1in !important;
}
/* Received DATE banner across the bottom (user, 2026-10-19) — mirrors the
   ZPL label so a pallet's age reads from across the aisle. */
.date-banner {
    flex-shrink: 0;
    margin-top: 0.08in;
    border: 3px solid #111;
    text-align: center;
    font-size: 40px;
    font-weight: 900;
    letter-spacing: 0.06em;
    line-height: 1.15;
}
.barcode-text {
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 0.06em;
    margin-top: 4px;
}
.footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    font-size: 14px;
    flex-shrink: 0;
}
.footer .received-block {
    display: flex;
    flex-direction: column;
    gap: 1px;
}
.footer .received {
    color: #444;
}
.footer .received-by {
    color: #444;
    font-weight: 600;
}
.footer .counters {
    display: flex;
    gap: 0.3in;
    font-weight: 700;
}
.footer .counters .today {
    font-size: 18px;
    color: #ec6f20;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="header">
    ${JLI_LOGO_SVG}
    <div class="warehouse">${esc(opts.warehouse)}</div>
</div>
${stockTypeLine}
${supplierLine}
<div class="barcode-block">
    <svg id="barcode"></svg>
    <div class="barcode-text">${esc(barcodeValue)}</div>
</div>
<div class="footer">
    <div class="received-block">
        <div class="received">Received ${receivedStr}</div>
        ${operatorLine}
    </div>
    <div class="counters">
        <div class="batch">${opts.batchIndex} / ${opts.batchTotal} in batch</div>
        <div class="today">#${dailySeqStr} today</div>
    </div>
</div>
<div class="date-banner">${dd}/${mm}/${yyyy}</div>
<script>
JsBarcode("#barcode", ${jsString(barcodeValue)}, {
    format: "CODE128",
    width: 3,
    height: 110,
    displayValue: false,
    margin: 0
});
<\/script>
</body>
</html>`;
}

export interface SalePalletLabelOpts {
    warehouse: string;
    colour: string;          // BLACK | GREY | RED | YELLOW
    palletType?: string | null;
    sizeLabel?: string | null;
    createdAt: string | Date;
    operator?: string | null;
    /** One-time put-away tote (PD-RED1-T) printed as a second barcode. */
    toteId?: string | null;
    /** Contents warnings (Alex, 2026-10-29): a warning triangle captioned
     *  LIQUIDS and/or GLASS between the SKU barcode and the tote barcode.
     *  Label only — never stored. */
    hazards?: { liquids?: boolean; glass?: boolean } | null;
}

/** The hazard captions to print, in label order; [] when none. */
export const palletHazardCaptions = (hazards: SalePalletLabelOpts["hazards"]): string[] => {
    const out: string[] = [];
    if (hazards?.liquids) out.push("LIQUIDS");
    if (hazards?.glass) out.push("GLASS");
    return out;
};

// Warning triangle as inline SVG for the HTML label: black triangle, white
// inset, exclamation drawn as shapes so it prints the same on any font set.
const HAZARD_TRIANGLE_SVG = `<svg viewBox="0 0 100 88" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
<path d="M50 2 L98 86 L2 86 Z" fill="#000"/>
<path d="M50 17 L86 79 L14 79 Z" fill="#fff"/>
<rect x="45.5" y="31" width="9" height="28" rx="3" fill="#000"/>
<circle cx="50" cy="68.5" r="5.5" fill="#000"/>
</svg>`;

/** Build a 6"x4" SALE pallet label (Goods In › Pallets). The pallet SKU
 *  (PD-RED1) is printed big and as a CODE128 barcode — it is the location
 *  barcode Put Away scans and the product SKU the pallet sells under. Used
 *  as the HTML fallback for non-Zebra printers; Zebra gets the agent's
 *  `name` layout (SKU big, barcode underneath). */
export function buildSalePalletLabel(sku: string, opts: SalePalletLabelOpts): string {
    const created = new Date(opts.createdAt);
    const dd = String(created.getDate()).padStart(2, "0");
    const mm = String(created.getMonth() + 1).padStart(2, "0");
    const yyyy = created.getFullYear();
    const hh = String(created.getHours()).padStart(2, "0");
    const min = String(created.getMinutes()).padStart(2, "0");
    const madeStr = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    const skuEsc = escapeHtmlText(sku);
    const toteEsc = escapeHtmlText((opts.toteId ?? "").trim());
    // Alex (2026-09-16): the label carries only the logo, the SKU (text +
    // barcode), the put-away tote barcode and the date — no warehouse,
    // colour / type / size or operator. The two barcodes stay well apart.
    const hasHazards = palletHazardCaptions(opts.hazards).length > 0;
    const toteBlock = toteEsc ? `<div class="${hasHazards ? "tote-block tote-block--tight" : "tote-block"}">
    <svg id="tote-barcode"></svg>
    <div class="tote-text">${toteEsc}</div>
</div>` : "";
    const toteScript = toteEsc ? `JsBarcode("#tote-barcode", "${toteEsc}", { format: "CODE128", width: 2, height: 60, displayValue: false, margin: 0 });` : "";
    // Contents warnings sit in the clear band between the SKU barcode and
    // the tote barcode; the tote block gives up most of its top gap so the
    // footer stays on the 6" page and nothing overlaps.
    const hazardCaptions = palletHazardCaptions(opts.hazards);
    const hazardBlock = hazardCaptions.length ? `<div class="hazard-block">
${hazardCaptions.map(c => `    <div class="hazard">${HAZARD_TRIANGLE_SVG}<div class="hazard-text">${c}</div></div>`).join("\n")}
</div>` : "";

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 4in 6in; margin: 0; }
body {
    width: 4in;
    height: 6in;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    display: flex;
    flex-direction: column;
    padding: 0.16in 0.2in;
}
.header {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 0.7in;
    flex-shrink: 0;
}
.header svg { height: 0.65in; width: auto; }
.sku {
    flex-shrink: 0;
    text-align: center;
    font-size: 36px;
    font-weight: 900;
    letter-spacing: 0.04em;
    line-height: 1.1;
    margin-top: 0.3in;
    white-space: nowrap;
}
.barcode-block {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.12in 0 0;
}
.barcode-block svg { width: 3.4in !important; height: 1.0in !important; }
.barcode-text {
    font-family: ui-monospace, "SF Mono", Menlo, monospace;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.06em;
    margin-top: 4px;
}
/* The put-away tote barcode sits in the lower half, a clear gap below the
   SKU barcode, so a scanner aimed at one can't catch the other. */
.tote-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    flex: 1 1 auto;
    margin-top: 0.6in;
}
.tote-block svg { width: 2.6in !important; height: 0.65in !important; }
.tote-block--tight { margin-top: 0.2in; }
.tote-text { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 18px; font-weight: 700; }
/* Contents warnings: one captioned triangle per hazard, side by side. */
.hazard-block {
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 0.5in;
    margin-top: 0.2in;
}
.hazard {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
}
.hazard svg { width: 0.7in; height: 0.6in; }
.hazard-text { font-size: 15px; font-weight: 900; letter-spacing: 0.08em; white-space: nowrap; }
.footer {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    font-size: 12px;
    color: #444;
    flex-shrink: 0;
    margin-top: 0.15in;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="header">
    ${JLI_LOGO_SVG}
</div>
<div class="sku">${skuEsc}</div>
<div class="barcode-block">
    <svg id="barcode"></svg>
    <div class="barcode-text">${skuEsc}</div>
</div>
${hazardBlock}
${toteBlock}
<div class="footer">
    <div>Made ${madeStr}</div>
</div>
<script>
JsBarcode("#barcode", "${skuEsc}", {
    format: "CODE128",
    width: 3,
    height: 90,
    displayValue: false,
    margin: 0
});
${toteScript}
<\/script>
</body>
</html>`;
}

// ZPL field data: ^ and ~ are command characters, \ is the ^FH escape.
const zplText = (s: string) => String(s ?? "").replace(/[\^~\\]/g, " ");

// Code 128 (subset B) width in modules: start + data + check codes at 11
// modules each, stop at 13. Used to centre the barcodes on the label.
const code128Modules = (value: string) => 11 * (value.length + 2) + 13;

/** 4"×6" portrait sale pallet label as raw ZPL (203 dpi → 812×1218 dots)
 *  for a local Zebra via the agent's /print-zpl (agent ≥ 1.6.0): JLI logo,
 *  the pallet SKU as text + CODE128, the one-time put-away tote barcode well
 *  below it, and the date made. Nothing else, by Alex's request. The HTML
 *  label (buildSalePalletLabel) is the same content for every other printer. */
export function buildSalePalletZpl(sku: string, opts: SalePalletLabelOpts): string {
    const W = 812;
    const created = new Date(opts.createdAt);
    const dd = String(created.getDate()).padStart(2, "0");
    const mm = String(created.getMonth() + 1).padStart(2, "0");
    const yyyy = created.getFullYear();
    const hh = String(created.getHours()).padStart(2, "0");
    const min = String(created.getMinutes()).padStart(2, "0");
    const madeStr = `Made ${dd}/${mm}/${yyyy} ${hh}:${min}`;
    const skuZ = zplText(sku);
    const toteId = zplText((opts.toteId ?? "").trim());
    // Alex (2026-09-16): logo, SKU (text + barcode), tote barcode, date only.
    // 72-dot font: PD-YELLOW10000 (14 chars) is ~625 dots wide at ≈0.62 × height.
    const skuFont = 72;

    const barcode = (value: string, y: number, height: number, maxBy: number) => {
        const modules = code128Modules(value);
        const by = Math.max(2, Math.min(maxBy, Math.floor(720 / modules)));
        const x = Math.max(0, Math.round((W - modules * by) / 2));
        return `^BY${by},3,${height}^FO${x},${y}^BCN,${height},N,N,N^FD${value}^FS`;
    };

    const logoX = Math.round((W - JLI_LOGO_ZPL_WIDTH) / 2);
    const lines: string[] = [
        "^XA", "^CI28", `^PW${W}`, "^LL1218", "^LH0,0", "^LS0",
        // Logo, centred
        `^FO${logoX},30^GFA,${JLI_LOGO_ZPL_TOTAL_BYTES},${JLI_LOGO_ZPL_TOTAL_BYTES},${JLI_LOGO_ZPL_BYTES_PER_ROW},${JLI_LOGO_ZPL_HEX}^FS`,
        // SKU
        `^FO0,270^FB${W},1,0,C^A0N,${skuFont},${skuFont}^FD${skuZ}^FS`,
        // SKU barcode + text
        barcode(skuZ, 380, 210, 5),
        `^FO0,605^FB${W},1,0,C^A0N,44,44^FD${skuZ}^FS`,
    ];
    // Contents warnings (Alex, 2026-10-29): one warning triangle per hazard
    // with LIQUIDS / GLASS under it, centred in the clear band between the
    // SKU text (ends ~650) and the tote barcode (starts 890): triangle
    // 690–810, caption 822–852, so nothing meets the text above or the bars
    // below. Two hazards sit side by side.
    const hazardCaptions = palletHazardCaptions(opts.hazards);
    if (hazardCaptions.length) {
        const triW = 138, triH = 120, stroke = 10, gap = 160, top = 690;
        const total = hazardCaptions.length * triW + (hazardCaptions.length - 1) * gap;
        let x = Math.round((W - total) / 2);
        for (const caption of hazardCaptions) {
            const half = triW / 2;
            lines.push(
                // Left edge "/", right edge "\", base bar, then the "!" as two blocks
                `^FO${x},${top}^GD${half},${triH},${stroke},B,R^FS`,
                `^FO${x + half},${top}^GD${half},${triH},${stroke},B,L^FS`,
                `^FO${x},${top + triH - stroke}^GB${triW},${stroke},${stroke}^FS`,
                `^FO${x + half - 6},${top + 42}^GB12,42,12^FS`,
                `^FO${x + half - 7},${top + 92}^GB14,14,14^FS`,
                `^FO${x - 60},${top + triH + 12}^FB${triW + 120},1,0,C^A0N,30,30^FD${caption}^FS`,
            );
            x += triW + gap;
        }
    }
    if (toteId) {
        // The tote barcode starts ~1.5" below the SKU barcode's bottom edge
        // (Alex: keep them apart so a scan can't catch the wrong one).
        lines.push(
            barcode(toteId, 890, 140, 3),
            `^FO0,1045^FB${W},1,0,C^A0N,40,40^FD${toteId}^FS`,
        );
    }
    lines.push(
        `^FO40,1140^A0N,28,28^FD${madeStr}^FS`,
        "^XZ",
    );
    return lines.join("\n");
}

/** 4"×6" portrait inbound pallet label as raw ZPL (203 dpi → 812×1218
 *  dots) for a local Zebra via the agent's /print-zpl (agent ≥ 1.6.0). Same
 *  content as buildPalletLabel (the HTML every other printer gets): JLI logo
 *  and warehouse across the top, the stock type big with the supplier under
 *  it, a wide CODE128 of the pallet ID with the ID printed beneath, then
 *  received time / operator on the left and the batch + daily counters on
 *  the right. Replaces the agent's generic 65×38 barcode layout, which
 *  clipped 19-character pallet IDs at the 65 mm print width (ZD230,
 *  2026-10-19) and printed the stock-type name across the bars (ZD421). */
export function buildPalletZpl(barcodeValue: string, opts: PalletLabelOpts): string {
    const W = 812;
    const margin = 36;
    const created = new Date(opts.createdAt);
    const dd = String(created.getDate()).padStart(2, "0");
    const mm = String(created.getMonth() + 1).padStart(2, "0");
    const yyyy = created.getFullYear();
    const hh = String(created.getHours()).padStart(2, "0");
    const min = String(created.getMinutes()).padStart(2, "0");
    const receivedStr = `Received ${dd}/${mm}/${yyyy} ${hh}:${min}`;
    const dailySeqStr = String(opts.dailySeq).padStart(3, "0");
    const valueZ = zplText(barcodeValue);
    const warehouseZ = zplText(opts.warehouse).toUpperCase();
    const stockTypeZ = zplText((opts.stockType ?? "").trim()).toUpperCase();
    const supplierZ = zplText((opts.supplier ?? "").trim()).toUpperCase();
    const operatorZ = zplText((opts.operator ?? "").trim());

    // Widest module (≤ 4) that keeps the bars inside the margins; a
    // 19-character ID lands on 3 dots (732 wide), the width the agent's
    // 65 mm layout could never fit.
    const modules = code128Modules(valueZ);
    const by = Math.max(2, Math.min(4, Math.floor((W - margin * 2) / modules)));
    const barcodeX = Math.max(margin, Math.round((W - modules * by) / 2));
    const barcodeH = 240;

    const logoY = 30;
    // Warehouse sits on the logo's vertical centre, right-aligned.
    const whFont = 58;
    const whY = logoY + Math.round((JLI_LOGO_ZPL_HEIGHT - whFont) / 2);
    const whX = margin + JLI_LOGO_ZPL_WIDTH + 20;

    let y = logoY + JLI_LOGO_ZPL_HEIGHT + 40;
    const lines: string[] = [
        "^XA", "^CI28", `^PW${W}`, "^LL1218", "^LH0,0", "^LS0",
        `^FO${margin},${logoY}^GFA,${JLI_LOGO_ZPL_TOTAL_BYTES},${JLI_LOGO_ZPL_TOTAL_BYTES},${JLI_LOGO_ZPL_BYTES_PER_ROW},${JLI_LOGO_ZPL_HEX}^FS`,
        `^FO${whX},${whY}^FB${W - whX - margin},1,0,R^A0N,${whFont},${whFont}^FD${warehouseZ}^FS`,
    ];
    if (stockTypeZ) {
        // Shrink long type names so they stay on one line (~0.55 × height per char).
        const typeFont = Math.max(48, Math.min(96, Math.floor((W - margin * 2) / (stockTypeZ.length * 0.58))));
        lines.push(`^FO${margin},${y}^FB${W - margin * 2},1,0,C^A0N,${typeFont},${typeFont}^FD${stockTypeZ}^FS`);
        y += typeFont + 12;
    }
    if (supplierZ) {
        const supFont = Math.max(32, Math.min(52, Math.floor((W - margin * 2) / (supplierZ.length * 0.58))));
        lines.push(`^FO${margin},${y}^FB${W - margin * 2},1,0,C^A0N,${supFont},${supFont}^FD${supplierZ}^FS`);
        y += supFont + 12;
    }
    y += 36;
    lines.push(
        `^BY${by},3,${barcodeH}^FO${barcodeX},${y}^BCN,${barcodeH},N,N,N^FD${valueZ}^FS`,
    );
    y += barcodeH + 24;
    const valueFont = Math.max(36, Math.min(58, Math.floor((W - margin * 2) / (valueZ.length * 0.62))));
    lines.push(`^FO${margin},${y}^FB${W - margin * 2},1,0,C^A0N,${valueFont},${valueFont}^FD${valueZ}^FS`);
    y += valueFont + 40;

    // Footer: received / operator left, batch + daily counters right.
    const halfW = Math.floor((W - margin * 2) / 2);
    const smallFont = 30;
    lines.push(`^FO${margin},${y}^FB${halfW},1,0,L^A0N,${smallFont},${smallFont}^FD${receivedStr}^FS`);
    lines.push(`^FO${margin + halfW},${y}^FB${halfW},1,0,R^A0N,${smallFont},${smallFont}^FD${opts.batchIndex} / ${opts.batchTotal} in batch^FS`);
    y += smallFont + 12;
    if (operatorZ) {
        lines.push(`^FO${margin},${y}^FB${halfW},1,0,L^A0N,${smallFont},${smallFont}^FDBy ${operatorZ}^FS`);
    }
    lines.push(`^FO${margin + halfW},${y}^FB${halfW},1,0,R^A0N,40,40^FD#${dailySeqStr} today^FS`);

    // Date banner across the empty bottom of the label (user, 2026-10-19):
    // a bordered box with the received DATE in the largest type that fits,
    // so a pallet's age reads from across the aisle. Stops 2mm clear of the
    // bottom edge for the ±1.5mm registration drift of desktop Zebras.
    const bannerH = 190;
    const bannerY = 1218 - 16 - bannerH;
    const dateFont = 110;
    lines.push(`^FO${margin},${bannerY}^GB${W - margin * 2},${bannerH},4^FS`);
    lines.push(`^FO${margin},${bannerY + Math.round((bannerH - dateFont) / 2)}^FB${W - margin * 2},1,0,C^A0N,${dateFont},${dateFont}^FD${dd}/${mm}/${yyyy}^FS`);
    lines.push("^XZ");
    return lines.join("\n");
}

/** Build an A5 landscape wall label for any warehouse location — goes
 *  up at the storage / inbound / area it represents and is scanned by
 *  operators on arrival. `displayWord` is the big bold word printed
 *  alongside the barcode (the location's display name, falling back to
 *  the type or barcode at the caller). Landscape gives the barcode a
 *  wider scannable area at typical wall-reading distances and lets the
 *  big word sit beside the bars rather than below, which reads more like
 *  a sign and less like a stacked label. Use `browserPrint(html)` to open
 *  the OS print dialog. */
/** Selectable sizes for the location wall label. Same vertically-centred
 *  stack (barcode → big word → warehouse) at every size — only the page
 *  dimensions and font/barcode scaling differ. "a5" is the historical
 *  default and the proportions for the other sizes mirror it. */
export type LocationLabelSize = "scansheet" | "dispatch" | "a5" | "a4";

interface LocationLabelDims {
    // Page is laid out landscape — long side first.
    pageWidthMm: number;
    pageHeightMm: number;
    // jsPDF format arg — string for standard paper, [w, h] tuple for thermal.
    pdfFormat: string | [number, number];
    // Barcode stripe.
    barcodeWidthMm: number;
    barcodeHeightMm: number;
    barcodeJsWidth: number;   // JsBarcode bar width
    barcodeJsHeight: number;  // JsBarcode SVG height (interpreted relative to width)
    // Big word + warehouse footer.
    bigWordFontPt: number;        // HTML font-size (pt)
    bigWordPdfFontPt: number;     // jsPDF setFontSize argument (pt)
    bigWordHeightMm: number;      // visual height — only used by PDF stack-centring math
    footerFontPt: number;         // HTML
    footerPdfFontPt: number;      // jsPDF
    footerHeightMm: number;       // visual height for stack maths
    barcodeToWordGapMm: number;
    wordToWarehouseGapMm: number;
}

const LOCATION_LABEL_DIMENSIONS: Record<LocationLabelSize, LocationLabelDims> = {
    // Small thermal scansheet label — same 65x38 the inbound barcode label uses.
    scansheet: {
        pageWidthMm: 65,  pageHeightMm: 38,  pdfFormat: [65, 38],
        barcodeWidthMm: 55, barcodeHeightMm: 14,
        barcodeJsWidth: 1.5, barcodeJsHeight: 60,
        bigWordFontPt: 14, bigWordPdfFontPt: 11, bigWordHeightMm: 4,
        footerFontPt: 6, footerPdfFontPt: 5, footerHeightMm: 2,
        barcodeToWordGapMm: 1.5, wordToWarehouseGapMm: 0.5,
    },
    // Dispatch flag-style label.
    dispatch: {
        pageWidthMm: 100, pageHeightMm: 60, pdfFormat: [100, 60],
        barcodeWidthMm: 90, barcodeHeightMm: 24,
        barcodeJsWidth: 2, barcodeJsHeight: 100,
        bigWordFontPt: 28, bigWordPdfFontPt: 22, bigWordHeightMm: 8,
        footerFontPt: 10, footerPdfFontPt: 8, footerHeightMm: 3.5,
        barcodeToWordGapMm: 2.5, wordToWarehouseGapMm: 0.7,
    },
    // A5 landscape — historical default. Numbers preserved exactly.
    a5: {
        pageWidthMm: 210, pageHeightMm: 148, pdfFormat: "a5",
        barcodeWidthMm: 190, barcodeHeightMm: 60,
        barcodeJsWidth: 3.5, barcodeJsHeight: 200,
        bigWordFontPt: 72, bigWordPdfFontPt: 54, bigWordHeightMm: 19,
        footerFontPt: 16, footerPdfFontPt: 16, footerHeightMm: 6,
        barcodeToWordGapMm: 6, wordToWarehouseGapMm: 1,
    },
    // A4 landscape — wall signage.
    a4: {
        pageWidthMm: 297, pageHeightMm: 210, pdfFormat: "a4",
        barcodeWidthMm: 270, barcodeHeightMm: 85,
        barcodeJsWidth: 4, barcodeJsHeight: 280,
        bigWordFontPt: 100, bigWordPdfFontPt: 75, bigWordHeightMm: 27,
        footerFontPt: 22, footerPdfFontPt: 22, footerHeightMm: 8,
        barcodeToWordGapMm: 8, wordToWarehouseGapMm: 1.5,
    },
};

export const LOCATION_LABEL_SIZES: LocationLabelSize[] = ["scansheet", "dispatch", "a5", "a4"];

export function buildLocationLabel(
    barcodeValue: string,
    opts: { warehouse: string; displayWord: string; size?: LocationLabelSize },
): string {
    // Display word + warehouse are always uppercased on the printed label
    // for consistent shelf-signage typography. Underlying data is not
    // mutated — barcodes are case-sensitive and stay as-is.
    const word = opts.displayWord.toUpperCase();
    const warehouseUpper = opts.warehouse.toUpperCase();
    const d = LOCATION_LABEL_DIMENSIONS[opts.size ?? "a5"];
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${d.pageWidthMm}mm ${d.pageHeightMm}mm; margin: 0; }
html, body { width: ${d.pageWidthMm}mm; height: ${d.pageHeightMm}mm; }
/* Single centred stack: barcode → big word → warehouse. Flex-centring
   the body means the whole group sits visually in the middle of the
   page, and tight margins between word + warehouse make them read as
   one unit (label + the warehouse it belongs to) rather than two
   separate footer rows. */
body {
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    width: ${d.pageWidthMm}mm;
    height: ${d.pageHeightMm}mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}
.barcode-block svg {
    width: ${d.barcodeWidthMm}mm !important;
    height: ${d.barcodeHeightMm}mm !important;
}
.big-word {
    margin-top: ${d.barcodeToWordGapMm}mm;
    font-size: ${d.bigWordFontPt}pt;
    font-weight: 900;
    letter-spacing: 0.04em;
    line-height: 1;
}
.footer {
    margin-top: ${d.wordToWarehouseGapMm}mm;
    font-size: ${d.footerFontPt}pt;
    color: #444;
    line-height: 1;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="barcode-block">
    <svg id="barcode"></svg>
</div>
<div class="big-word">${esc(word)}</div>
<div class="footer">${esc(warehouseUpper)}</div>
<script>
JsBarcode("#barcode", ${jsString(barcodeValue)}, {
    format: "CODE128",
    width: ${d.barcodeJsWidth},
    height: ${d.barcodeJsHeight},
    displayValue: false,
    margin: 0
});
<\/script>
</body>
</html>`;
}

/** Generate the location wall label as a downloadable A5 landscape PDF
 *  that mirrors `buildLocationLabel`. Used as a "Download" alternative
 *  to the OS print dialog — so warehouse managers can email or archive
 *  the label, and Linux/Chrome hosts that don't expose Save-as-PDF still
 *  have a path to a PDF file. Uses jsPDF + jsbarcode (lazy-loaded). */
export async function downloadLocationLabelPdf(
    barcodeValue: string,
    opts: { warehouse: string; displayWord: string; filename?: string; size?: LocationLabelSize },
): Promise<{ success: boolean; error?: string }> {
    try {
        const [{ default: JsPDF }, { default: JsBarcode }] = await Promise.all([
            import("jspdf"),
            import("jsbarcode"),
        ]);

        const d = LOCATION_LABEL_DIMENSIONS[opts.size ?? "a5"];
        const PAGE_W_MM = d.pageWidthMm;
        const PAGE_H_MM = d.pageHeightMm;
        const doc = new JsPDF({ unit: "mm", format: d.pdfFormat as any, orientation: "landscape" });

        // Layout matches buildLocationLabel: a single vertically centred
        // stack (barcode → big word → warehouse) with tight spacing
        // between the word and warehouse so they read as one unit.
        const totalHeight =
            d.barcodeHeightMm + d.barcodeToWordGapMm +
            d.bigWordHeightMm + d.wordToWarehouseGapMm +
            d.footerHeightMm;
        const stackTop = (PAGE_H_MM - totalHeight) / 2;

        // Barcode — render with JsBarcode to a canvas, embed as image.
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, barcodeValue, {
            format: "CODE128",
            width: d.barcodeJsWidth,
            height: d.barcodeJsHeight,
            displayValue: false,
            margin: 0,
        });
        const dataUrl = canvas.toDataURL("image/png");
        const barcodeX = (PAGE_W_MM - d.barcodeWidthMm) / 2;
        const barcodeY = stackTop;
        doc.addImage(dataUrl, "PNG", barcodeX, barcodeY, d.barcodeWidthMm, d.barcodeHeightMm);

        // Big bold word — "INBOUND" / "PRZYJĘCIE" / etc. — under the barcode.
        // y is the text baseline, so add the visual word height for placement.
        const wordY = barcodeY + d.barcodeHeightMm + d.barcodeToWordGapMm + d.bigWordHeightMm;
        doc.setTextColor(17, 17, 17);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(d.bigWordPdfFontPt);
        doc.text(opts.displayWord.toUpperCase(), PAGE_W_MM / 2, wordY, { align: "center" });

        // Warehouse name — sits just under the big word, no gap to bottom.
        const warehouseY = wordY + d.wordToWarehouseGapMm + d.footerHeightMm;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(d.footerPdfFontPt);
        doc.setTextColor(68, 68, 68);
        doc.text(opts.warehouse.toUpperCase(), PAGE_W_MM / 2, warehouseY, { align: "center" });

        const filename = opts.filename ?? `location-${opts.warehouse}.pdf`;
        doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
        return { success: true };
    } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "PDF generation failed" };
    }
}

/** Bundle a list of named-barcode labels into a single multi-page PDF and
 *  trigger a browser download. One label per page at the same 65x38mm
 *  format as `buildNamedBarcodeLabel` so the output prints identically on
 *  a label printer; users who only have A4 can pick "fit multiple per
 *  sheet" in their PDF viewer.
 *
 *  Why a download (vs. browserPrint → "Save as PDF"): admins back up label
 *  sets to email or archive without poking the print dialog, and the
 *  Linux/Chrome OS print stack doesn't reliably expose Save-as-PDF.
 */
export async function downloadLabelsPdf(
    labels: Array<{ barcode: string; name: string }>,
    filename: string,
): Promise<{ success: boolean; error?: string }> {
    if (!labels.length) return { success: false, error: "No labels" };
    try {
        // Lazy-load — keeps the bundle size off the critical path. These
        // are only pulled in when an admin actually clicks "Download all".
        const [{ default: JsPDF }, { default: JsBarcode }] = await Promise.all([
            import("jspdf"),
            import("jsbarcode"),
        ]);

        const PAGE_W_MM = 65;
        const PAGE_H_MM = 38;
        const doc = new JsPDF({ unit: "mm", format: [PAGE_W_MM, PAGE_H_MM], orientation: "landscape" });

        labels.forEach((label, i) => {
            if (i > 0) doc.addPage([PAGE_W_MM, PAGE_H_MM], "landscape");

            // Render the barcode to an offscreen canvas at a fixed pixel
            // size, then drop it into the PDF as an image. JsBarcode's
            // CODE128 output is the same as the print HTML uses, so the
            // scannable result is identical.
            const canvas = document.createElement("canvas");
            JsBarcode(canvas, label.barcode, {
                format: "CODE128",
                width: 2,
                height: 60,
                displayValue: false,
                margin: 0,
            });
            const dataUrl = canvas.toDataURL("image/png");

            const barcodeWidthMm = 60;
            const barcodeHeightMm = 22;
            const barcodeX = (PAGE_W_MM - barcodeWidthMm) / 2;
            const barcodeY = 4;
            doc.addImage(dataUrl, "PNG", barcodeX, barcodeY, barcodeWidthMm, barcodeHeightMm);

            // Human-readable barcode value beneath the bars (matches the
            // .barcode-text on the printed label).
            doc.setFont("courier", "bold");
            doc.setFontSize(12);
            doc.text(label.barcode, PAGE_W_MM / 2, barcodeY + barcodeHeightMm + 4, { align: "center" });

            // Big bold name at the bottom — same role as .name-text.
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text(label.name.toUpperCase(), PAGE_W_MM / 2, PAGE_H_MM - 3, { align: "center" });
        });

        doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
        return { success: true };
    } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "PDF generation failed" };
    }
}

/** Build a 65x38mm barcode label that also shows a human-readable name.
 *  Used by the Dispatch admin desks/boxes — operators can read "DESK 1"
 *  or "B" at a glance, but the scannable value stays the unique
 *  generated barcode (e.g. PDDESK1 / PEMBOX3).
 *
 *  Two layouts:
 *  - default: stacked — barcode value prominent, name small underneath
 *    (desks, scansheet locations — the value IS the identity).
 *  - emphasizeName: side-by-side — bars fill the LEFT ~75% at full label
 *    height and the box NAME owns the RIGHT ~25% at maximum size,
 *    auto-shrinking only if a long name overflows. No value text.
 *    Boxes are grabbed by their size name. */
export function buildNamedBarcodeLabel(
    barcodeValue: string,
    name: string,
    opts: { emphasizeName?: boolean; widthMm?: number; heightMm?: number } = {},
): string {
    // Label size defaults to 65×38 but is overridable — box labels follow
    // the warehouse's Wipe label size (wipe_label_width_mm/height_mm) so
    // both modules print on the same stock.
    const w = opts.widthMm && opts.widthMm > 0 ? opts.widthMm : 65;
    const h = opts.heightMm && opts.heightMm > 0 ? opts.heightMm : 38;

    if (opts.emphasizeName === true) {
        // Side-by-side geometry: bars on the LEFT at full label height
        // (tall bars scan best on wide-short stock like 70×30), the box
        // name HORIZONTAL on the RIGHT in the largest type the column
        // takes. The split adapts to the name: 75/25 for a single
        // character, 60/40 for two or more — a wider column keeps
        // multi-char names huge without stacking. Wide names squeeze
        // horizontally (scaleX) but keep their full height. No value
        // text — the name and the bars are the whole label.
        const contentW = w - 4;                                // 2mm padding each side
        const contentH = h - 4;                                // 2mm padding top/bottom
        const nameLen = Math.max(1, String(name).trim().length);
        const nameFrac = nameLen >= 2 ? 0.40 : 0.25;
        const nameW = Math.floor(contentW * nameFrac);         // right column
        const barsW = contentW - nameW - 1;                    // left column (1mm gap)
        const nameFontPx = Math.floor(contentH * 3.5);         // ~mm→px at 96dpi with breathing room
        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${w}mm ${h}mm; margin: 0; }
body {
    width: ${w}mm;
    height: ${h}mm;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 1mm;
    font-family: Arial, Helvetica, sans-serif;
    padding: 2mm;
}
svg {
    width: ${barsW}mm !important;
    height: ${contentH}mm !important;
}
.name-cell {
    width: ${nameW}mm;
    height: ${contentH}mm;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}
.name-text {
    font-size: ${nameFontPx}px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<svg id="barcode"></svg>
<div class="name-cell"><div class="name-text" id="nm">${esc(name)}</div></div>
<script>
JsBarcode("#barcode", ${jsString(barcodeValue)}, {
    format: "CODE128",
    width: 2,
    height: 80,
    displayValue: false,
    margin: 0
});
// Stretch the bars to fill their column — vertical stretch is harmless
// for a 1D barcode and horizontal scaling is uniform, so module ratios
// (the scannable part) are preserved.
document.getElementById("barcode").setAttribute("preserveAspectRatio", "none");
// A name wider than its column squeezes horizontally only — height
// stays full so the letters keep their size. Mirrors the ZPL layout's
// independent char-width cap.
(function () {
    var el = document.getElementById("nm");
    var cell = el && el.parentElement;
    if (!el || !cell) return;
    if (el.scrollWidth > cell.clientWidth) {
        el.style.transformOrigin = "center center";
        el.style.transform = "scaleX(" + (cell.clientWidth / el.scrollWidth) * 0.95 + ")";
    }
})();
<\/script>
</body>
</html>`;
    }

    const barsMaxH = Math.max(8, h - 16);
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${w}mm ${h}mm; margin: 0; }
body {
    width: ${w}mm;
    height: ${h}mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: Arial, Helvetica, sans-serif;
    padding: 2mm;
}
svg {
    width: ${w - 5}mm !important;
    height: auto !important;
    max-height: ${barsMaxH}mm;
}
.barcode-text {
    font-size: 16px;
    font-weight: 700;
    font-family: monospace;
    letter-spacing: 0.10em;
    margin-top: 2px;
    text-align: center;
}
.name-text {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 4px;
    text-align: center;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<svg id="barcode"></svg>
<div class="barcode-text">${esc(barcodeValue)}</div>
<div class="name-text">${esc(name)}</div>
<script>
JsBarcode("#barcode", ${jsString(barcodeValue)}, {
    format: "CODE128",
    width: 2,
    height: 60,
    displayValue: false,
    margin: 0
});
<\/script>
</body>
</html>`;
}

/** The label stock these sorting slot labels were designed on. Every dimension
 *  in the layout below is expressed as a fraction of this, so a warehouse can
 *  set its own stock size (settings.sorting_label_width_mm / _height_mm) and the
 *  whole label scales rather than getting clipped. */
export const BAY_LABEL_BASE = { widthMm: 185, heightMm: 35 } as const;

/** Stock the high/low RACKING is labelled with (Warehouse › Locations print).
 *  The same 185 x 35mm strip as the sorting labels (Alex measured it,
 *  2026-09-29, after first saying 80mm). Held separately from BAY_LABEL_BASE
 *  all the same: the racking printer is a different machine, and its size is
 *  a separate per-warehouse setting (racking_label_*_mm). */
export const RACKING_LABEL_BASE = { widthMm: 185, heightMm: 35 } as const;

export interface BayLabelSize { widthMm?: number | null; heightMm?: number | null }

// Helvetica Bold metrics, as ratios of the font size. Cap height is what a
// letter or digit actually occupies vertically (there are no descenders in
// A–E or 0–9), and digits are all one width in this face.
const CAP_HEIGHT = 0.717;
const DIGIT_WIDTH = 0.556;
// Fraction of a coloured block the type fills. Applied to the HEIGHT as a
// ceiling on the font size itself — not on the cap height — because the HTML
// label centres a line box of exactly font-size, so anything taller than the
// block overflows and clips the glyph.
const SQUARE_FILL = 0.88;
// The number block is wider than the shelf block so a two-digit position
// (10–18 on the put wall) fits at the SAME type size as a single letter,
// rather than shrinking to fit and leaving the pair looking mismatched.
const POSITION_BOX_RATIO = 1.18;
// The least width the bars are given before the coloured blocks start to
// shrink. A 10-character location code ("NP-A-1-1-1") is 145 CODE128 modules,
// so 36mm is a 0.25mm module — the floor for reliable scanning on the
// handhelds. On the 185mm sorting strip this never binds; it is what keeps the
// 80mm racking strip scannable.
const BARCODE_MIN_MM = 36;

/** Resolve a requested stock size to the geometry the label is drawn against.
 *  Blocks are as tall as the label; the shelf block is square and the number
 *  block a little wider. On short-and-wide stock they'd eat the whole label, so
 *  the shelf block is capped at a quarter of the width and the middle keeps
 *  its room. On NARROW stock (the 80mm racking strip) a quarter of the width
 *  still leaves the bars too little room, so the blocks give way further until
 *  the barcode has BARCODE_MIN_MM — never below an eighth of the width each,
 *  past which the bars shrink instead. */
export function bayLabelGeometry(size?: BayLabelSize) {
    const W = Number(size?.widthMm) > 0 ? Number(size!.widthMm) : BAY_LABEL_BASE.widthMm;
    const H = Number(size?.heightMm) > 0 ? Number(size!.heightMm) : BAY_LABEL_BASE.heightMm;
    const midPadMm = 2 * (H / 35);
    const arrowColW = 24 / 185 * W;
    const roomForBlocks = (W - arrowColW - 2 * midPadMm - BARCODE_MIN_MM) / (1 + POSITION_BOX_RATIO);
    const sq = Math.min(H, W / 4, Math.max(roomForBlocks, W / 8));
    const posSq = sq * POSITION_BOX_RATIO;
    const squaresW = sq + posSq;
    const rest = Math.max(W - squaresW, 1);    // width left for barcode + arrow
    // Fractions read off the original 185x35 artwork: the middle band ran
    // 70→185mm, with the barcode image at 76..136 and the arrow around 168.
    // `x` maps one of those original mm positions into the resized band.
    const x = (mmAt185: number) => squaresW + (mmAt185 - 70) / 115 * rest;
    // One size for both blocks — each is checked against its own block width,
    // and POSITION_BOX_RATIO is chosen so two digits clear it at full size.
    const fontMm = (text: string | number, boxW: number) => Math.min(
        H * SQUARE_FILL,
        (boxW * SQUARE_FILL) / (Math.max(String(text).length, 1) * DIGIT_WIDTH),
    );
    // jsPDF draws from the baseline, so centring the cap-height block has to be
    // derived; the HTML label centres itself with flexbox.
    const baseline = (mm: number) => (H + CAP_HEIGHT * mm) / 2;
    // What the bars may be drawn at in the HTML label: their share of the
    // 185mm original, widened to the floor on narrow stock, but never more
    // than the middle column actually has once the arrow and padding are out.
    const midContentW = W - squaresW - arrowColW - 2 * midPadMm;
    const barcodeGapMm = 1 * (H / 35);
    const barcodeFontPx = 16 * (H / 35);
    return {
        W, H, sq, posSq, squaresW, rest,
        // horizontal positions inside the middle band
        barcodeX: x(76), barcodeW: 60 / 115 * rest,
        arrowCx: x(168), arrowHalfW: 7 / 115 * rest,
        stemX: x(166), stemW: 4 / 115 * rest,
        // vertical positions, all proportional to the label height
        barcodeY: 3 / 35 * H, barcodeH: 23 / 35 * H,
        barcodeTextY: 31 / 35 * H,
        arrowBaseY: 15 / 35 * H, arrowApexY: 5 / 35 * H,
        stemY: 14 / 35 * H, stemH: 12 / 35 * H,
        // type sizes scale with the height so they stay in proportion
        barcodeFontPt: 12 * (H / 35), barcodeFontPx: barcodeFontPx,
        midPadMm, barcodeSvgW: Math.min(midContentW, Math.max(70 / 185 * W, BARCODE_MIN_MM)),
        barcodeSvgMaxH: 26 / 35 * H,
        barcodeGapMm,
        arrowColW, arrowSvgW: 16 / 185 * W, arrowSvgH: 24 / 35 * H,
        shelfFontMm: (text: string | number) => fontMm(text, sq),
        positionFontMm: (text: string | number) => fontMm(text, posSq),
        shelfBaseline: (text: string | number) => baseline(fontMm(text, sq)),
        positionBaseline: (text: string | number) => baseline(fontMm(text, posSq)),
    };
}

/** mm → PostScript points, for jsPDF's setFontSize. */
export const mmToPt = (mm: number) => mm * 72 / 25.4;

// Shared with the scan screens — see helpers/labelContrast.
export { readableTextOn } from "./labelContrast";

export interface BayLabelInput {
    barcode: string;
    label?: string;
    shelf: string;
    position: number | string;
    shelfColour?: string | null;
    positionColour?: string | null;
}

/** Build a single Pick & Sort / Sorter bay-location label, 185mm x 35mm by
 *  default. A large coloured block behind the shelf letter, a wider one behind
 *  the position number, then the scannable CODE128 barcode + its
 *  human-readable value, and an up-arrow on the right.
 *
 *  ONE label per document, and the caller sends ONE PRINT JOB PER LABEL. A
 *  printer that cuts at end of job then cuts each label, which is what the
 *  warehouse wants. A multi-page variant was tried for continuous uncut output
 *  and reverted — it needed printer-side media changes that weren't worth it. */
export function buildBayLocationLabel(opts: BayLabelInput & { size?: BayLabelSize }): string {
    const { barcode, shelf, position } = opts;
    const shelfBg = opts.shelfColour || "#2563eb";
    const posBg = opts.positionColour || "#6b7280";
    const g = bayLabelGeometry(opts.size);
    const mm = (n: number) => `${Math.round(n * 1000) / 1000}mm`;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${mm(g.W)} ${mm(g.H)}; margin: 0; }
body {
    width: ${mm(g.W)};
    height: ${mm(g.H)};
    display: flex;
    flex-direction: row;
    align-items: stretch;
    font-family: Arial, Helvetica, sans-serif;
}
.sq {
    height: ${mm(g.H)};
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    line-height: 1;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.sq-shelf {
    width: ${mm(g.sq)};
    background: ${shelfBg};
    color: ${readableTextOn(shelfBg)};
    font-size: ${mm(g.shelfFontMm(shelf))};
}
.sq-pos {
    width: ${mm(g.posSq)};
    background: ${posBg};
    color: ${readableTextOn(posBg)};
    font-size: ${mm(g.positionFontMm(position))};
}
.mid {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${mm(g.midPadMm)};
}
.mid svg { width: ${mm(g.barcodeSvgW)} !important; height: auto !important; max-height: ${mm(g.barcodeSvgMaxH)}; }
.barcode-text {
    font-size: ${Math.round(g.barcodeFontPx * 10) / 10}px;
    font-weight: 700;
    font-family: monospace;
    letter-spacing: 0.10em;
    margin-top: ${mm(g.barcodeGapMm)};
}
.arrow {
    width: ${mm(g.arrowColW)};
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.arrow svg { width: ${mm(g.arrowSvgW)}; height: ${mm(g.arrowSvgH)}; }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="sq sq-shelf">${esc(shelf)}</div>
<div class="sq sq-pos">${esc(position)}</div>
<div class="mid">
    <svg id="barcode"></svg>
    <div class="barcode-text">${esc(barcode)}</div>
</div>
<div class="arrow">
    <svg viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg"><path d="M20 2 L38 26 L26 26 L26 58 L14 58 L14 26 L2 26 Z" fill="#000"/></svg>
</div>
<script>
JsBarcode("#barcode", ${jsString(barcode)}, { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
<\/script>
</body>
</html>`;
}

/** Stock the compact mono slot label is designed for. */
export const COMPACT_BAY_LABEL_BASE = { widthMm: 93, heightMm: 47 } as const;

/** How the compact label is rotated on the page, to match the way the stock
 *  feeds through the printer. Same vocabulary as the wipe label. */
export type CompactLabelOrientation = "normal" | "cw" | "ccw" | "flip";
export const COMPACT_LABEL_ORIENTATIONS: CompactLabelOrientation[] = ["normal", "cw", "ccw", "flip"];

/** Geometry for the compact label. Separate from bayLabelGeometry on purpose —
 *  it's a different layout, not a resize of the wide one, and the wide one must
 *  not change.
 *
 *  Letter and number side by side on the left, full height, barcode on the
 *  right. Their widths are split 1:2, NOT the wide label's 1:1.18 — at full
 *  height the type here is limited by block WIDTH rather than height, so the
 *  number block has to be exactly twice the letter's for "A" and "18" to come
 *  out the same size. (1.18 is the right ratio only when height is the limit.)
 *
 *  Block column width is the one real trade-off on a 93mm label: every extra
 *  millimetre of type costs the barcode a millimetre, and the barcode is
 *  already the tighter constraint. 44mm lands type at 21.6mm with a 0.336mm
 *  module — bigger type than a stacked layout gave, and still well clear of
 *  the ~0.25mm floor for reliable scanning. */
export function compactBayLabelGeometry(size?: BayLabelSize) {
    const W = Number(size?.widthMm) > 0 ? Number(size!.widthMm) : COMPACT_BAY_LABEL_BASE.widthMm;
    const H = Number(size?.heightMm) > 0 ? Number(size!.heightMm) : COMPACT_BAY_LABEL_BASE.heightMm;
    const blocksW = W * (44 / 93);
    const shelfW = blocksW / 3;
    const posW = blocksW - shelfW;              // 1:2, so both glyphs match
    const blockH = H;                           // side by side, full height
    const pad = 2 * (W / 93);
    const barcodeFontPx = 13 * (H / 47);
    const barcodeGapMm = 1 * (H / 47);
    const barcodeTextLineMm = (barcodeFontPx * 25.4 / 96) * 1.2;
    // A little more breathing room than the wide label's SQUARE_FILL: these
    // blocks are narrow, so the same fill would leave the glyph almost touching
    // the edges.
    const fill = 0.82;
    // ONE size for both blocks — whichever of the two is the binding constraint
    // wins, so the letter and the number are always drawn at the same size.
    const fontMm = (text: string | number) => Math.min(
        blockH * fill,
        (shelfW * fill) / DIGIT_WIDTH,
        (posW * fill) / (Math.max(String(text).length, 2) * DIGIT_WIDTH),
    );
    return {
        W, H, blocksW, shelfW, posW, blockH, pad,
        barcodeAreaW: W - blocksW,
        // Bars are generated at their normal intrinsic height (90) here, unlike
        // the earlier stacked layout: the barcode area is now tall and narrow,
        // so a normal aspect is what makes every code WIDTH-limited and fill
        // the area. Width-limited is the goal either way — it maximises module
        // width, which is what keeps a 93mm label scannable.
        barcodeBoxW: W - blocksW - 2 * pad,
        barcodeBoxH: H - 2 * pad - barcodeTextLineMm - barcodeGapMm,
        barcodeGapMm, barcodeFontPx, barcodeTextLineMm,
        shelfFontMm: fontMm,
        positionFontMm: fontMm,
    };
}

/** Build a compact 93 x 47mm slot label for a MONO / thermal printer.
 *
 *  A separate label type, not a variant of the wide one — the 185 x 35mm
 *  colour design is untouched. Differences, all deliberate:
 *    • black type on the bare label, no filled blocks and no colour, so it
 *      prints on a direct-thermal printer and uses the least ink possible;
 *    • no arrow — dropped as non-essential to fit the narrower stock;
 *    • shelf letter and position number side by side on the left, barcode right.
 *
 *  The slot is still identified by its letter, number and barcode; what's lost
 *  versus the colour label is colour-coding, which a mono printer can't carry
 *  anyway. */
export function buildCompactBayLabel(
    opts: BayLabelInput & { size?: BayLabelSize; orientation?: CompactLabelOrientation },
): string {
    const { barcode, shelf, position } = opts;
    const g = compactBayLabelGeometry(opts.size);
    const mm = (n: number) => `${Math.round(n * 1000) / 1000}mm`;
    // Same approach as the wipe label: the readable content is always laid out
    // at W x H inside .rot and rotated to suit how the stock feeds through the
    // printer. For the quarter-turns the printed page footprint is swapped so
    // the rotated content fills it; centring means nothing clips.
    const rotDeg = { normal: 0, cw: 90, ccw: -90, flip: 180 }[opts.orientation ?? "normal"] ?? 0;
    const swap = rotDeg === 90 || rotDeg === -90;
    const pageW = swap ? g.H : g.W;
    const pageH = swap ? g.W : g.H;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${mm(pageW)} ${mm(pageH)}; margin: 0; }
html, body { width: ${mm(pageW)}; height: ${mm(pageH)}; }
body { position: relative; overflow: hidden; }
.rot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${mm(g.W)};
    height: ${mm(g.H)};
    transform: translate(-50%, -50%) rotate(${rotDeg}deg);
    transform-origin: center center;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    font-family: Arial, Helvetica, sans-serif;
}
.ids { width: ${mm(g.blocksW)}; flex: none; display: flex; flex-direction: row; }
/* Black type on the bare label — no filled blocks. Beyond looking cleaner this
   is what makes the label cheap to print: the filled version put ~47% of the
   label under solid ink, which is heavy for a thermal head and was what drained
   the colour printer. Here the only ink is the glyphs and the barcode. */
.sq {
    height: ${mm(g.blockH)};
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-weight: 800;
    line-height: 1;
    font-size: ${mm(g.positionFontMm(position))};
}
.sq-shelf { width: ${mm(g.shelfW)}; }
.sq-pos { width: ${mm(g.posW)}; }
.bar {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${mm(g.pad)};
}
.bar svg { width: ${mm(g.barcodeBoxW)} !important; height: ${mm(g.barcodeBoxH)} !important; }
.barcode-text {
    font-size: ${Math.round(g.barcodeFontPx * 10) / 10}px;
    font-weight: 700;
    font-family: monospace;
    letter-spacing: 0.08em;
    margin-top: ${mm(g.barcodeGapMm)};
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="rot">
    <div class="ids">
        <div class="sq sq-shelf">${esc(shelf)}</div>
        <div class="sq sq-pos">${esc(position)}</div>
    </div>
    <div class="bar">
        <svg id="barcode"></svg>
        <div class="barcode-text">${esc(barcode)}</div>
    </div>
</div>
<script>
JsBarcode("#barcode", ${jsString(barcode)}, { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
<\/script>
</body>
</html>`;
}

/** Stock the large-value slot label is designed for. */
export const LARGE_SLOT_LABEL_BASE = { widthMm: 70, heightMm: 30 } as const;

/** The same large-value design scaled up for 152 x 70mm stock. Stated exactly
 *  as the stock is cut (152 x 70) — unlike the 6x4 this is not an inch size,
 *  so there is no fractional conversion to preserve. */
export const LARGE_SLOT_LABEL_152_BASE = { widthMm: 152, heightMm: 70 } as const;

/** 6 x 4 inch label stock — the same media the dispatch shipping labels print
 *  on (see buildPrintDocument, `@page { size: 4in 6in }`), which is loaded
 *  PORTRAIT in those printers.
 *
 *  This is the design footprint, so it is stated the way the content reads:
 *  152.4mm across. Printing it on portrait-loaded stock is a quarter-turn,
 *  which is why this design defaults to a rotated orientation rather than
 *  "normal" — the rotation swaps the page back to 101.6 x 152.4.
 *
 *  Exact inch conversions on purpose: a printer set to 6x4 expects
 *  152.4 x 101.6mm, and rounding to 152 x 102 shifts the image on a printer
 *  that centres it. */
export const BAND_6X4_LABEL_BASE = { widthMm: 152.4, heightMm: 101.6 } as const;
/** …and the band the content is confined to, centred on that stock. The rest of
 *  the label is deliberately left blank: 28.3mm clear on either side of it. */
export const BAND_6X4_CONTENT = { widthMm: 152.4, heightMm: 45 } as const;

// Arial Bold advance width for a capital letter, in em. Digits are all
// DIGIT_WIDTH (0.556); A-E are noticeably wider at 0.722. The other two
// layouts put the letter and the number in their own blocks and size both off
// DIGIT_WIDTH, which is near enough there. This label draws them as ONE string
// ("A1"), so the letter's real width has to be counted — sizing "A1" as two
// digits overruns the column by ~6% and clips the glyph.
const LETTER_WIDTH = 0.722;
const valueEm = (text: string) => [...text]
    .reduce((sum, ch) => sum + (/[0-9]/.test(ch) ? DIGIT_WIDTH : LETTER_WIDTH), 0);

/** Geometry for the large-value label: the display value on the left as big as
 *  it will go, a full-height barcode on the right, nothing else.
 *
 *  A third layout, not a resize of either of the others. What it drops is what
 *  pays for the type size — no colour blocks, no arrow, and no human-readable
 *  barcode line, so the value column runs the full height of the label and the
 *  bars take every millimetre left over, top to bottom.
 *
 *  The value column is 40% of the width. That is the one real trade-off: every
 *  millimetre it gains costs the barcode a millimetre, and the barcode is the
 *  tighter constraint. 40% lands a two-character value at ~19mm on 70x30 stock
 *  while leaving the longest code we print (8 characters, e.g. NPS207A10) at a
 *  0.31mm module — clear of the ~0.25mm floor for reliable CODE128 scanning,
 *  but not by enough to widen the column much further. */
export function largeSlotLabelGeometry(size?: BayLabelSize) {
    const W = Number(size?.widthMm) > 0 ? Number(size!.widthMm) : LARGE_SLOT_LABEL_BASE.widthMm;
    const H = Number(size?.heightMm) > 0 ? Number(size!.heightMm) : LARGE_SLOT_LABEL_BASE.heightMm;
    const pad = 2 * (W / 70);
    const valueW = W * 0.4;
    // Applied to the font size rather than the cap height, for the same reason
    // as SQUARE_FILL: the label centres a line box of exactly font-size, so
    // anything taller than the column overflows.
    const fill = 0.88;
    // Two characters is the floor for sizing, so a hypothetical one-character
    // value doesn't come out at double the size of every other label.
    const valueFontMm = (text: string | number) => Math.min(
        H * fill,
        (valueW * fill) / Math.max(valueEm(String(text)), LETTER_WIDTH + DIGIT_WIDTH),
    );
    return {
        W, H, pad, valueW,
        // Bars fill the column left over, at their intrinsic aspect — every
        // code comes out WIDTH-limited, which maximises the module width and
        // is what keeps a small label scannable.
        barcodeBoxW: W - valueW - 2 * pad,
        barcodeBoxH: H - 2 * pad,
        valueFontMm,
    };
}

/** Build a single large-value slot label, 70 x 30mm by default.
 *
 *  Designed for the Pembroke Pick & Sort racking, where the display value and
 *  the scannable code are NOT the same string — Bay 2's shelves read A1-D6 but
 *  encode A7-D12. The operator reads the big value; the scanner reads the bars.
 *  Printing the barcode's own value underneath would put two different-looking
 *  identifiers on one label, so it is deliberately left off. */
export function buildLargeSlotLabel(
    opts: BayLabelInput & { size?: BayLabelSize; page?: BayLabelSize; orientation?: CompactLabelOrientation },
): string {
    const { barcode, shelf, position } = opts;
    const value = opts.label || `${shelf}${position}`;
    const g = largeSlotLabelGeometry(opts.size);
    const mm = (n: number) => `${Math.round(n * 1000) / 1000}mm`;
    // Same rotation handling as the compact label — the readable content is
    // always laid out at W x H inside .rot, and quarter-turns swap the printed
    // page footprint so the rotated content fills it.
    const rotDeg = { normal: 0, cw: 90, ccw: -90, flip: 180 }[opts.orientation ?? "normal"] ?? 0;
    const swap = rotDeg === 90 || rotDeg === -90;
    // The page can be LARGER than the design — that's how the 6x4 band prints a
    // 45mm strip of content on a 101.6mm label with a deliberate blank gap
    // either side. .rot is already centred in the page, so the gap falls out of
    // the difference and needs no extra machinery. Defaults to the design size,
    // which is every other caller. A page SMALLER than the design would spill
    // content off the label (and make a label printer emit a blank between
    // every real one), so callers pass stock they know fits.
    const pW = Number(opts.page?.widthMm) > 0 ? Number(opts.page!.widthMm) : g.W;
    const pH = Number(opts.page?.heightMm) > 0 ? Number(opts.page!.heightMm) : g.H;
    const pageW = swap ? pH : pW;
    const pageH = swap ? pW : pH;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${mm(pageW)} ${mm(pageH)}; margin: 0; }
html, body { width: ${mm(pageW)}; height: ${mm(pageH)}; }
body { position: relative; overflow: hidden; }
.rot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${mm(g.W)};
    height: ${mm(g.H)};
    transform: translate(-50%, -50%) rotate(${rotDeg}deg);
    transform-origin: center center;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    font-family: Arial, Helvetica, sans-serif;
}
/* Black type on the bare label — no filled blocks, so it prints on a
   direct-thermal head and uses the least ink possible. Arial at 800 rather
   than Arial Black on purpose: the geometry is sized off Arial Bold advance
   widths, and Black's wider glyphs would overflow the column. The hairline
   stroke buys back the extra weight without changing the advance. */
.val {
    width: ${mm(g.valueW)};
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
    -webkit-text-stroke: 0.02em currentColor;
    font-size: ${mm(g.valueFontMm(value))};
}
.bar {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${mm(g.pad)};
}
.bar svg { width: ${mm(g.barcodeBoxW)} !important; height: ${mm(g.barcodeBoxH)} !important; }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="rot">
    <div class="val">${esc(value)}</div>
    <div class="bar"><svg id="barcode"></svg></div>
</div>
<script>
JsBarcode("#barcode", ${jsString(barcode)}, { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
<\/script>
</body>
</html>`;
}

/** Which sorting slot label design to render. One list shared by the print
 *  modal's design picker and the bay download menu, so the two can't drift
 *  apart. "standard" is the 185x35 colour label (resizable per warehouse);
 *  the rest are mono designs whose size is fixed by the design. */
export type SlotLabelStyle = "standard" | "compact" | "large" | "large152" | "band6x4";
export const SLOT_LABEL_STYLES: SlotLabelStyle[] = ["standard", "compact", "large", "large152", "band6x4"];

// Scope one label document's CSS under `scope` so several labels can share a
// page without their rules colliding (two labels of the same design differ in
// font size and colour under the SAME class names). The builders' CSS is
// simple and hand-written — flat rules, one @page, comments — which is all
// this handles: at-rules are dropped (the sheet owns @page), `html`/`body`
// become the scope itself (the wrapper takes the body's role), and the `*`
// reset covers the wrapper and its descendants.
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function scopeLabelCss(css: string, scope: string): string {
    const stripped = css.replace(/\/\*[\s\S]*?\*\//g, "");
    const out: string[] = [];
    const rule = /([^{}]+)\{([^{}]*)\}/g;
    let m: RegExpExecArray | null;
    while ((m = rule.exec(stripped))) {
        const selector = m[1].trim();
        const decls = m[2].replace(/\s+/g, " ").trim();
        if (!selector || selector.startsWith("@") || !decls) continue;
        const scoped = selector.split(",").map(s => s.trim()).filter(Boolean).map(s => {
            if (s === "html" || s === "body") return scope;
            if (s === "*") return `${scope}, ${scope} *`;
            return `${scope} ${s}`;
        });
        out.push(`${Array.from(new Set(scoped)).join(", ")} { ${decls} }`);
    }
    return out.join("\n");
}

/** How many labels one print job carries at most. A run is batched into ONE
 *  job so a printer that cuts at the end of a job (Epson TM-C3500) delivers
 *  it as a single strip — but a 300-label racking run as one 300-page PDF
 *  would sit in the agent's render/spool step long enough to trip its
 *  timeouts, so very long runs go as a few strips instead. */
export const LABELS_PER_PRINT_JOB = 100;

/** Merge single-label documents — exactly what the build*Label functions
 *  return — into ONE multi-page document, one label per page, so a whole run
 *  prints as a single job and a printer set to cut at the end of the job
 *  (Epson TM-C3500, Pembroke racking) cuts once, after the last label.
 *
 *  Each label keeps its own CSS, scoped under a per-design class (identical
 *  CSS is emitted once), its element ids and the scripts that target them are
 *  suffixed per label, and the single JsBarcode include is hoisted so the
 *  print agent's cached-script injection (which replaces the FIRST include)
 *  still covers every barcode. The page size is the first label's @page; the
 *  callers always batch one design at one size. Every page but the last
 *  carries a forced break, and each page clips its own overflow, so a label
 *  can't spill a blank page into the run. One document is returned as-is. */
export function buildLabelSheet(docs: string[]): string {
    if (docs.length === 0) throw new Error("buildLabelSheet: no labels");
    if (docs.length === 1) return docs[0];
    const pageSize = /@page\s*\{\s*size:\s*([^;}]+);/i.exec(docs[0])?.[1]?.trim();
    if (!pageSize) throw new Error("buildLabelSheet: first label has no @page size");
    const [pageW, pageH] = pageSize.split(/\s+/);
    if (!pageW || !pageH) throw new Error(`buildLabelSheet: unexpected @page size "${pageSize}"`);

    const cssClassByText = new Map<string, string>();
    const styles: string[] = [];
    const pages: string[] = [];
    const scripts: string[] = [];
    docs.forEach((doc, i) => {
        const css = /<style>([\s\S]*?)<\/style>/i.exec(doc)?.[1] ?? "";
        let body = /<body[^>]*>([\s\S]*)<\/body>/i.exec(doc)?.[1] ?? "";
        let labelScripts: string[] = [];
        body = body.replace(/<script>([\s\S]*?)<\/script>/gi, (_, s: string) => { labelScripts.push(s.trim()); return ""; });

        const suffix = `-${i + 1}`;
        const ids = Array.from(new Set(Array.from(body.matchAll(/\sid="([^"]+)"/g), x => x[1])));
        for (const id of ids) {
            const idRe = escapeRegExp(id);
            body = body.replace(new RegExp(`\\sid="${idRe}"`, "g"), ` id="${id}${suffix}"`);
            labelScripts = labelScripts.map(s => s
                .replace(new RegExp(`(["'])#${idRe}\\1`, "g"), `$1#${id}${suffix}$1`)
                .replace(new RegExp(`getElementById\\((["'])${idRe}\\1\\)`, "g"), `getElementById($1${id}${suffix}$1)`));
        }

        let cls = cssClassByText.get(css);
        if (!cls) {
            cls = `lbl-${cssClassByText.size + 1}`;
            cssClassByText.set(css, cls);
            styles.push(scopeLabelCss(css, `.${cls}`));
        }
        pages.push(`<div class="label-page ${cls}">\n${body.trim()}\n</div>`);
        scripts.push(...labelScripts);
    });

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${pageW} ${pageH}; margin: 0; }
html, body { width: ${pageW}; margin: 0; padding: 0; }
.label-page {
    width: ${pageW};
    height: ${pageH};
    overflow: hidden;
    position: relative;
    break-inside: avoid;
    break-after: page;
    page-break-after: always;
}
.label-page:last-child { break-after: auto; page-break-after: auto; }
${styles.join("\n")}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
${pages.join("\n")}
<script>
${scripts.join("\n")}
<\/script>
</body>
</html>`;
}

/** Bundle Pick & Sort bay-location labels into a multi-page PDF and download
 *  it — one page per label, page = the label stock. Renders whichever of the
 *  slot label designs is asked for, from the SAME geometry the HTML print
 *  path uses, so a downloaded sheet and a printed run come out identical.
 *  `size` is the per-warehouse stock size and applies to "standard" only —
 *  every other design fixes its own size. */
export async function downloadBayLabelsPdf(
    labels: Array<{ barcode: string; label: string; shelf: string; position: number | string; shelf_colour?: string | null; position_colour?: string | null }>,
    filename: string,
    size?: BayLabelSize,
    style: SlotLabelStyle = "standard",
): Promise<{ success: boolean; error?: string }> {
    if (!labels.length) return { success: false, error: "No labels" };
    try {
        const [{ default: JsPDF }, { default: JsBarcode }] = await Promise.all([
            import("jspdf"),
            import("jsbarcode"),
        ]);

        const barcodePng = (value: string) => {
            const canvas = document.createElement("canvas");
            JsBarcode(canvas, value, { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
            return canvas.toDataURL("image/png");
        };
        // jsPDF draws text from the baseline; centring a cap-height block in a
        // band of height `boxH` starting at `top` is the same derivation the
        // HTML labels get from flexbox.
        const capBaseline = (top: number, boxH: number, fontMm: number) => top + (boxH + CAP_HEIGHT * fontMm) / 2;

        let doc: InstanceType<typeof JsPDF>;

        if (style === "compact") {
            // The 93x47 mono design: letter + number side by side on the left,
            // barcode and its readable value on the right.
            const g = compactBayLabelGeometry();
            doc = new JsPDF({ unit: "mm", format: [g.W, g.H], orientation: "landscape" });
            const textFontMm = g.barcodeFontPx * 25.4 / 96;
            labels.forEach((label, i) => {
                if (i > 0) doc.addPage([g.W, g.H], "landscape");
                // ONE size for both blocks, exactly as the HTML label does.
                const fontMm = g.positionFontMm(label.position);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(mmToPt(fontMm));
                const baseline = capBaseline(0, g.H, fontMm);
                doc.text(String(label.shelf), g.shelfW / 2, baseline, { align: "center" });
                doc.text(String(label.position), g.shelfW + g.posW / 2, baseline, { align: "center" });
                doc.addImage(barcodePng(label.barcode), "PNG", g.blocksW + g.pad, g.pad, g.barcodeBoxW, g.barcodeBoxH);
                doc.setFont("courier", "bold");
                doc.setFontSize(mmToPt(textFontMm));
                doc.text(label.barcode, g.blocksW + g.pad + g.barcodeBoxW / 2,
                    capBaseline(g.pad + g.barcodeBoxH + g.barcodeGapMm, g.barcodeTextLineMm, textFontMm), { align: "center" });
            });
        } else if (style === "large" || style === "large152" || style === "band6x4") {
            // The large-value design at its three stocks. The 6x4 band draws
            // the 45mm content strip centred on the 101.6mm label — same blank
            // gap either side as the HTML path gets from centring .rot.
            const stock = style === "large" ? LARGE_SLOT_LABEL_BASE
                : style === "large152" ? LARGE_SLOT_LABEL_152_BASE
                    : BAND_6X4_LABEL_BASE;
            const content = style === "band6x4" ? BAND_6X4_CONTENT : stock;
            const g = largeSlotLabelGeometry(content);
            const offY = (stock.heightMm - g.H) / 2;
            doc = new JsPDF({ unit: "mm", format: [stock.widthMm, stock.heightMm], orientation: "landscape" });
            labels.forEach((label, i) => {
                if (i > 0) doc.addPage([stock.widthMm, stock.heightMm], "landscape");
                // The DISPLAY value, not the barcode's value — on Pembroke Bay 2
                // the two differ, and the design deliberately shows only this.
                const value = label.label || `${label.shelf}${label.position}`;
                const fontMm = g.valueFontMm(value);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(mmToPt(fontMm));
                doc.text(value, g.valueW / 2, capBaseline(offY, g.H, fontMm), { align: "center" });
                doc.addImage(barcodePng(label.barcode), "PNG", g.valueW + g.pad, offY + g.pad, g.barcodeBoxW, g.barcodeBoxH);
            });
        } else {
            // Standard 185x35 colour design, at the per-warehouse stock size.
            const g = bayLabelGeometry(size);
            const PAGE_W_MM = g.W;
            const PAGE_H_MM = g.H;
            doc = new JsPDF({ unit: "mm", format: [PAGE_W_MM, PAGE_H_MM], orientation: "landscape" });

            const hexToRgb = (hex: string | null | undefined, fallback: [number, number, number]): [number, number, number] => {
                if (!hex) return fallback;
                const h = hex.replace("#", "");
                const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
                if (full.length < 6) return fallback;
                return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
            };

            labels.forEach((label, i) => {
                if (i > 0) doc.addPage([PAGE_W_MM, PAGE_H_MM], "landscape");

                // Two coloured squares (shelf letter, position number).
                const [sr, sg, sb] = hexToRgb(label.shelf_colour, [37, 99, 235]);
                const [pr, pg, pb] = hexToRgb(label.position_colour, [107, 114, 128]);
                const shelfHex = label.shelf_colour || "#2563eb";
                const posHex = label.position_colour || "#6b7280";
                doc.setFillColor(sr, sg, sb);
                doc.rect(0, 0, g.sq, g.H, "F");
                doc.setFillColor(pr, pg, pb);
                doc.rect(g.sq, 0, g.posSq, g.H, "F");
                doc.setFont("helvetica", "bold");
                // Each block gets whichever of black/white contrasts more with it,
                // and a baseline derived from its own type size so the glyph sits
                // optically centred rather than at a fixed height.
                doc.setTextColor(readableTextOn(shelfHex));
                doc.setFontSize(mmToPt(g.shelfFontMm(label.shelf)));
                doc.text(String(label.shelf), g.sq / 2, g.shelfBaseline(label.shelf), { align: "center" });
                doc.setTextColor(readableTextOn(posHex));
                doc.setFontSize(mmToPt(g.positionFontMm(label.position)));
                doc.text(String(label.position), g.sq + g.posSq / 2, g.positionBaseline(label.position), { align: "center" });

                // Barcode in the middle — taller and a little narrower than the
                // squares so it reads cleanly from a distance.
                doc.addImage(barcodePng(label.barcode), "PNG", g.barcodeX, g.barcodeY, g.barcodeW, g.barcodeH);
                doc.setTextColor(0, 0, 0);
                doc.setFont("courier", "bold");
                doc.setFontSize(g.barcodeFontPt);
                doc.text(label.barcode, g.barcodeX + g.barcodeW / 2, g.barcodeTextY, { align: "center" });

                // Up arrow on the right — drawn as vector (triangle head + stem)
                // because jsPDF's core fonts don't carry the ↑ glyph.
                doc.setFillColor(0, 0, 0);
                doc.triangle(g.arrowCx - g.arrowHalfW, g.arrowBaseY, g.arrowCx + g.arrowHalfW, g.arrowBaseY, g.arrowCx, g.arrowApexY, "F");
                doc.rect(g.stemX, g.stemY, g.stemW, g.stemH, "F");
            });
        }

        doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
        return { success: true };
    } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : "PDF generation failed" };
    }
}

/** Build a 65x38mm barcode label for product re-labelling */
export function buildBarcodeLabel(barcodeValue: string): string {
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 65mm 38mm; margin: 0; }
body {
    width: 65mm;
    height: 38mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: Arial, Helvetica, sans-serif;
    padding: 1mm;
}
svg {
    width: 60mm !important;
    height: auto !important;
    max-height: 30mm;
}
.barcode-text {
    font-size: 20px;
    font-weight: 700;
    font-family: monospace;
    letter-spacing: 0.12em;
    margin-top: 2px;
    text-align: center;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<svg id="barcode"></svg>
<div class="barcode-text">${esc(barcodeValue)}</div>
<script>
JsBarcode("#barcode", ${jsString(barcodeValue)}, {
    format: "CODE128",
    width: 2,
    height: 80,
    displayValue: false,
    margin: 0
});
<\/script>
</body>
</html>`;
}

export type WipeLabelOrientation = "normal" | "cw" | "ccw" | "flip";

export interface WipeLabelOpts {
    widthMm?: number;          // per-warehouse label width (default 65)
    heightMm?: number;         // per-warehouse label height (default 38)
    dispose?: boolean;         // render a prominent DISPOSE marker
    orientation?: WipeLabelOrientation; // print rotation (default 'ccw')
}

/** Build a Wipe-module relabel label at a configurable per-warehouse size.
 *  CODE128 barcode of the new relabel barcode, the (mixed-case) barcode text,
 *  and — for the dispose outcome — a bold DISPOSE banner so handlers route it
 *  correctly. */
export function buildWipeLabel(barcodeValue: string, opts: WipeLabelOpts = {}): string {
    const w = opts.widthMm && opts.widthMm > 0 ? opts.widthMm : 65;
    const h = opts.heightMm && opts.heightMm > 0 ? opts.heightMm : 38;
    const esc = (s: string) => String(s).replace(/[<>&]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c] as string));
    const disposeBanner = opts.dispose ? `<div class="dispose">DISPOSE</div>` : "";
    // Rotation is per-warehouse (Settings ▸ Wipe). The readable, landscape
    // content is always laid out at w × h in .rot and rotated to taste; for the
    // quarter-turns the printed page footprint is swapped so it fills cleanly.
    const rotDeg = { normal: 0, cw: 90, ccw: -90, flip: 180 }[opts.orientation ?? "ccw"] ?? -90;
    const swap = rotDeg === 90 || rotDeg === -90;
    const pageW = swap ? h : w;
    const pageH = swap ? w : h;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
/* Per-warehouse print orientation. The readable landscape content lives in .rot
   (always w × h) and is rotated by the configured amount; for the quarter-turns
   the page footprint is swapped so the rotated content fills it. Centring means
   any rotation lands cleanly with no clipping. Isolated to the wipe label. */
@page { size: ${pageW}mm ${pageH}mm; margin: 0; }
html, body { width: ${pageW}mm; height: ${pageH}mm; margin: 0; padding: 0; }
body { position: relative; overflow: hidden; }
.rot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${w}mm;
    height: ${h}mm;
    transform: translate(-50%, -50%) rotate(${rotDeg}deg);
    transform-origin: center center;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-family: Arial, Helvetica, sans-serif;
    padding: 1.5mm;
}
.dispose {
    flex: 0 0 auto;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.15em;
    color: #b91c1c;
    border: 2px solid #b91c1c;
    border-radius: 3px;
    padding: 0 6px;
    margin-bottom: 1mm;
}
/* The barcode wrap takes all the height left over after the (optional) DISPOSE
   banner and the barcode text, so the bars are as tall as the label allows —
   taller bars scan more reliably. Side padding preserves the quiet zone. */
.barcode-wrap { flex: 1 1 auto; width: 100%; min-height: 0; padding: 0 3mm; display: flex; }
#barcode { width: 100%; height: 100%; display: block; }
.barcode-text {
    flex: 0 0 auto;
    font-size: 24px;
    font-weight: 700;
    font-family: monospace;
    letter-spacing: 0.12em;
    margin-top: 1mm;
    text-align: center;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="rot">
${disposeBanner}
<div class="barcode-wrap"><svg id="barcode"></svg></div>
<div class="barcode-text">${esc(barcodeValue)}</div>
</div>
<script>
JsBarcode("#barcode", "${esc(barcodeValue)}", {
    format: "CODE128",
    width: 2,
    height: 100,
    displayValue: false,
    margin: 0
});
// Make the rendered barcode stretch to fill the wrap (full width + remaining
// height). Stretching all bars by the same factor preserves the relative bar
// widths, so scannability is unaffected while the bars get much taller.
(function () {
    var s = document.getElementById("barcode");
    var bw = s.getAttribute("width"), bh = s.getAttribute("height");
    if (bw && bh) {
        s.setAttribute("viewBox", "0 0 " + bw + " " + bh);
        s.removeAttribute("width");
        s.removeAttribute("height");
        s.setAttribute("preserveAspectRatio", "none");
    }
})();
<\/script>
</body>
</html>`;
}

/** Fall back to the browser's native print dialog, which uses whatever the
 *  OS has set as the default printer. This works even when the local print
 *  agent isn't installed. Returns { success, error? }. */
export async function browserPrint(html: string): Promise<{ success: boolean; error?: string }> {
    return new Promise(resolve => {
        try {
            const win = window.open("", "_blank", "width=700,height=500");
            if (!win) return resolve({ success: false, error: "Popup blocked" });
            win.document.write(html);
            win.document.close();
            // Wait for images / barcode SVGs to render before triggering print.
            win.onload = () => {
                win.focus();
                win.print();
                // Some browsers fire onafterprint, some don't — close after a
                // short delay so the print dialog has time to appear.
                const cleanup = () => { try { win.close(); } catch {} resolve({ success: true }); };
                if ("onafterprint" in win) {
                    win.onafterprint = cleanup;
                } else {
                    setTimeout(cleanup, 1000);
                }
            };
            // If onload doesn't fire (rare), still clean up.
            setTimeout(() => resolve({ success: true }), 3000);
        } catch (err: any) {
            resolve({ success: false, error: err?.message || "Browser print failed" });
        }
    });
}

/** Build an HTML document containing N flag labels for printing.
 *  Each label shows the flagged ID as a barcode + text, the order number,
 *  the flag reason, and "N of M" box numbering. */
export function buildFlaggedLabels(opts: {
    flaggedId: string;
    orderNumber: string;
    reason: string;
    labelCount: number;
}): string {
    const { flaggedId, orderNumber, reason, labelCount } = opts;
    const labels = Array.from({ length: labelCount }, (_, i) => {
        const boxLabel = labelCount > 1 ? `${i + 1} of ${labelCount}` : "1 of 1";
        return `
        <div class="flag-label">
            <div class="flag-label-header">HOT DESK</div>
            <svg id="bc-${i}"></svg>
            <div class="flag-label-id">${esc(flaggedId)}</div>
            <div class="flag-label-order">${esc(orderNumber)}</div>
            <div class="flag-label-reason">${esc(reason)}</div>
            <div class="flag-label-box">Box ${boxLabel}</div>
        </div>`;
    }).join("\n");

    // JsBarcode invocations — one per label
    const barcodeScripts = Array.from({ length: labelCount }, (_, i) =>
        `JsBarcode("#bc-${i}", ${jsString(flaggedId)}, { format: "CODE128", width: 2, height: 80, displayValue: false, margin: 0 });`
    ).join("\n");

    // 4in × 6in PORTRAIT — the exact same page setup as the dispatch
    // shipping label (buildPrintDocument), so flag labels print cleanly
    // on the same label stock the desks are loaded with.
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 4in 6in; margin: 0; }
body { font-family: Arial, Helvetica, sans-serif; color: #000; width: 4in; display: flex; flex-direction: column; align-items: center; }
.flag-label {
    page-break-after: always;
    width: 4in;
    height: 6in;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8mm 5mm;
    border: 2px solid #000;
}
.flag-label-header {
    font-size: 40px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    color: #c00;
    margin-bottom: 6px;
}
.flag-label svg { width: 88mm !important; height: auto !important; max-height: 32mm; }
.flag-label-id {
    font-size: 30px;
    font-weight: 900;
    font-family: monospace;
    letter-spacing: 0.1em;
    margin-top: 4px;
    text-align: center;
    word-break: break-all;
}
.flag-label-order { font-size: 22px; font-weight: 700; margin-top: 4px; text-align: center; }
.flag-label-reason { font-size: 17px; color: #555; margin-top: 2px; text-align: center; }
.flag-label-box { font-size: 26px; font-weight: 800; margin-top: 10px; }
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
${labels}
<script>
${barcodeScripts}
<\/script>
</body>
</html>`;
}

/** Build a full HTML document from label innerHTML + CSS for the print agent.
 *  Dispatch shipping labels are 4" wide x 6" high PORTRAIT — matching the
 *  6x4 label stock loaded portrait in the dispatch label printers. */
export function buildPrintDocument(labelHtml: string): string {
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 4in 6in; margin: 0; }
body { font-family: Arial, Helvetica, sans-serif; color: #000; width: 4in; display: flex; flex-direction: column; align-items: center; }

.dispatch-print-label {
    page-break-after: always;
    width: 4in;
    height: 6in;
    box-sizing: border-box;
    padding: 8px;
    border: 2px solid #000;
    overflow: hidden;
    font-size: 11px;
}

.dpl-carrier-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #000;
    color: #fff;
    padding: 5px 8px;
    margin: -8px -8px 6px;
}
.dpl-carrier-name { font-size: 14px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.08em; }
.dpl-carrier-service { font-size: 9px; font-weight: 700; text-transform: uppercase; background: #fff; color: #000; padding: 2px 6px; border-radius: 2px; }

.dpl-tracking-barcode { display: flex; justify-content: center; padding: 4px 0 6px; border-bottom: 1px solid #000; }
/* Barcode SVGs render at fixed pixel widths — scale down (never up) so long
   tracking numbers still fit inside the 4in portrait width without clipping. */
.dpl-tracking-barcode svg, .dpl-sort-barcode svg { max-width: 100%; height: auto; }

.dpl-address-section { display: flex; gap: 10px; padding: 6px 0; border-bottom: 1px solid #ccc; }
.dpl-address-from, .dpl-address-to { flex: 1; line-height: 1.4; }
.dpl-address-from { font-size: 8px; color: #555; border-right: 1px solid #ccc; padding-right: 10px; }
.dpl-address-to { font-size: 11px; }
.dpl-address-label { font-size: 7px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #999; margin: 0 0 2px; }
.dpl-address-from p, .dpl-address-to p { margin: 0; }

.dpl-info-bar { display: flex; border-bottom: 1px solid #ccc; }
.dpl-info-cell { flex: 1; padding: 4px 3px; text-align: center; border-right: 1px solid #eee; }
.dpl-info-cell:last-child { border-right: none; }
.dpl-info-label { display: block; font-size: 6px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #999; }
.dpl-info-value { display: block; font-size: 10px; font-weight: 800; margin-top: 1px; }

.dpl-items { display: flex; flex-wrap: wrap; gap: 4px; padding: 4px 0; border-bottom: 1px solid #ccc; }
.dpl-item { font-size: 8px; font-family: monospace; background: #f0f0f0; padding: 1px 4px; border-radius: 2px; }

.dpl-sort-barcode { display: flex; justify-content: center; padding: 4px 0 2px; }

/* COLLECTION label (components/dispatch/collection-label.tsx) — a collection
   order's parcel is handed over at the counter, so the label carries the order
   number as a barcode and the customer's name, and nothing that could be
   mistaken for a courier label. Same 4in × 6in stock. */
.dispatch-print-label--collection { display: flex; flex-direction: column; padding: 0; }
.dpl-collection-banner { background: #000; color: #fff; text-align: center; font-size: 34px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; padding: 14px 8px; }
.dpl-collection-order { display: flex; flex-direction: column; align-items: center; padding: 12px 8px 4px; }
.dpl-collection-order-number { font-size: 30px; font-weight: 900; letter-spacing: 0.04em; }
.dpl-collection-barcode { display: flex; justify-content: center; padding: 4px 8px 12px; border-bottom: 2px solid #000; }
.dpl-collection-barcode svg { max-width: 100%; height: auto; }
.dpl-collection-customer { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 12px 8px; border-bottom: 1px solid #000; }
.dpl-collection-customer-name { font-size: 24px; font-weight: 800; line-height: 1.15; word-break: break-word; }
.dpl-collection-info { display: flex; border-bottom: 1px solid #000; }
.dpl-collection-cell { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 8px 4px; border-right: 1px solid #ccc; }
.dpl-collection-cell:last-child { border-right: none; }
.dpl-collection-label { display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #555; }
.dpl-collection-value { display: block; font-size: 16px; font-weight: 800; margin-top: 2px; }
.dpl-collection-footer { margin-top: auto; text-align: center; font-size: 20px; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; padding: 10px 8px; border-top: 2px solid #000; }
</style>
</head>
<body>
${labelHtml}
</body>
</html>`;
}

/** A Raspberry Pi print agent reports "<protocol>-pi.<build>" (e.g.
 *  "1.6.3-pi.2026101902"). Only those devices have the remote network /
 *  device control channel on Admin › Printers. */
export function isPiAgent(version: string | null | undefined): boolean {
    return /-pi(\.\d+)?$/.test(version ?? "");
}

// ---------------------------------------------------------------------------
// Courier pallet label (Dispatch › Conveyor Unload › Close pallet, Alex
// 2026-10-19/21). 6x4 stock, portrait. Top to bottom:
//
//   1. The courier, large, white on a solid black bar ("APC", "INPOST",
//      "DPD", "CARGUS") — the highlighted part, the first thing people see.
//   2. Pembroke: "ZONE" (APC postal zones, "6/7/8") or "SERVICE" (InPost
//      24 / 48) over the classifier, large. Poland has none, so the
//      courier sits alone in the space.
//   3. The barcode, with no value text.
//   4. The pallet identifier ("PD-INPOST-2": site code, courier, that
//      courier's own number) directly under the barcode, small, plain black
//      text — no bold, no background (Alex, 2026-10-21). It is exactly what
//      the bars encode, and unique across sites (dispatch_pallets.label has
//      a unique index).
//   5. Who printed it and when.
//
// (Alex, 2026-10-21: "the first thing people see is the courier and the
// service, then the pallet sku at the bottom".)
// ---------------------------------------------------------------------------

export interface CourierPalletLabelOpts {
    barcode: string;      // PD-APC-12 — scanned by the forklift driver
    sku: string;          // "APC - 12"
    classifier: string;   // "4" / "6/7/8" / "48" / "" (catch-all, or Poland)
    madeAt?: string | Date;         // when the pallet was closed; not printed
    printedBy?: string | null;      // the user who pressed print
    printedAt?: string | Date;      // the date on the label; defaults to now
}

// "DPD - 3" → { name: "DPD", number: "3" }. A sku without " - <n>" keeps
// its whole text as the name.
export const splitCourierPalletSku = (sku: string): { name: string; number: string } => {
    const m = /^(.*?)\s*-\s*(\d+)\s*$/.exec(String(sku ?? ""));
    return m ? { name: m[1], number: m[2] } : { name: String(sku ?? ""), number: "" };
};

// The sku as printed, courier in capitals ("INPOST - 12").
export const courierPalletSkuText = (sku: string): string => {
    const { name, number } = splitCourierPalletSku(sku);
    return number ? `${name.toUpperCase()} - ${number}` : name.toUpperCase();
};

// The caption over the classifier: InPost's 24 / 48 is a service, APC's
// sort codes are postal zones.
export const courierPalletClassifierCaption = (sku: string): string =>
    (splitCourierPalletSku(sku).name.trim().toUpperCase() === "INPOST" ? "SERVICE" : "ZONE");

// The one footer line: who printed this label, and when.
const courierPalletFooter = (opts: CourierPalletLabelOpts): string => {
    const by = String(opts.printedBy ?? "").trim();
    const at = fmtMade(opts.printedAt ?? new Date());
    return by ? `Printed by ${by} · ${at}` : `Printed ${at}`;
};

const fmtMade = (when: string | Date): string => {
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
};

// Sizes. The courier line is one fixed size for every courier (fits six
// capitals, "CARGUS" / "INPOST"); only a longer name shrinks, never wraps.
// Bold Arial capitals / digits average ~0.7em per character; ~320px fits
// across (Zebra: ~0.62 of height, ~700 dots).
const COURIER = { px: 64, dots: 140 };
const capPx = (text: string, max: number): number => Math.min(max, Math.floor(320 / (Math.max(1, text.length) * 0.7)));
const capDots = (text: string, max: number): number => Math.min(max, Math.floor(700 / (Math.max(1, text.length) * 0.62)));

export const courierPalletNamePx = (name: string): number => capPx(name, COURIER.px);
export const courierPalletNameDots = (name: string): number => capDots(name, COURIER.dots);
// The classifier is fixed per position (a zone never changes), so it may
// size to its own length.
export const courierPalletClassifierPx = (classifier: string): number =>
    Math.max(48, Math.min(110, Math.floor(340 / (Math.max(1, classifier.length) * 0.64))));
export const courierPalletClassifierDots = (classifier: string): number =>
    Math.max(100, Math.min(230, Math.floor(760 / (Math.max(1, classifier.length) * 0.62))));

/** 4"x6" portrait courier pallet label as HTML (network printers, local
 *  non-Zebra, browser dialog). */
export function buildCourierPalletLabel(opts: CourierPalletLabelOpts): string {
    const barcode = escapeHtmlText(opts.barcode);
    const classifier = escapeHtmlText(opts.classifier ?? "");
    const footer = escapeHtmlText(courierPalletFooter(opts));
    const name = splitCourierPalletSku(opts.sku).name.toUpperCase();
    const zone = classifier
        ? `
<div class="caption caption--zone">${courierPalletClassifierCaption(opts.sku)}</div>
<div class="classifier" style="font-size: ${courierPalletClassifierPx(opts.classifier)}px">${classifier}</div>`
        : "";
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: 4in 6in; margin: 0; }
body {
    width: 4in;
    height: 6in;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.2in 0.2in 0.18in;
}
/* The courier and its zone / service: the first thing people read. */
.head {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}
/* The highlighted part: the courier, white on a solid black bar. */
.courier {
    width: 100%;
    padding: 10px 10px 12px;
    background: #000;
    color: #fff;
    font-weight: 900;
    line-height: 1;
    letter-spacing: 0.02em;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.caption {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.12em;
    color: #333;
}
.caption--zone { margin-top: 0.14in; }
.classifier {
    font-weight: 900;
    line-height: 1;
    white-space: nowrap;
}
.barcode-block {
    display: flex;
    justify-content: center;
}
.barcode-block svg { width: 3.4in !important; height: 1in !important; }
/* The barcode's value, directly under it: small, plain, black. */
.barcode-value {
    margin-top: 3px;
    font-size: 13px;
    font-weight: 400;
    color: #000;
    letter-spacing: 0.06em;
    text-align: center;
    white-space: nowrap;
}
.footer {
    margin-top: 0.1in;
    width: 100%;
    font-size: 14px;
    font-weight: 700;
    color: #333;
    text-align: center;
    line-height: 1.35;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<div class="head">
<div class="courier" style="font-size: ${courierPalletNamePx(name)}px">${escapeHtmlText(name)}</div>${zone}
</div>
<div class="barcode-block"><svg id="barcode"></svg></div>
<div class="barcode-value">${escapeHtmlText(opts.barcode)}</div>
<div class="footer">${footer}</div>
<script>
JsBarcode("#barcode", "${barcode}", { format: "CODE128", width: 3, height: 100, displayValue: false, margin: 0 });
// Fill the full 3.4in x 1in box: keeping the proportions would make the
// bars of a longer value shorter, and short bars scan badly.
document.getElementById("barcode").setAttribute("preserveAspectRatio", "none");
<\/script>
</body>
</html>`;
}

/** The same label as raw ZPL for a local Zebra (203 dpi, 812x1218 dots). */
export function buildCourierPalletZpl(opts: CourierPalletLabelOpts): string {
    const W = 812;
    const classifier = zplText(opts.classifier ?? "");
    const footer = zplText(courierPalletFooter(opts));
    const modules = code128Modules(opts.barcode);
    const by = Math.max(2, Math.min(5, Math.floor(720 / modules)));
    const x = Math.max(0, Math.round((W - modules * by) / 2));
    const name = zplText(splitCourierPalletSku(opts.sku).name.toUpperCase());
    const nameDots = courierPalletNameDots(name);
    // Top block (0-580): the courier reversed out of a black bar, then the
    // zone / service. Without a classifier (Poland) the bar sits in the
    // middle of the block.
    const barH = nameDots + 50;
    const courierBar = (y: number) => [
        `^FO40,${y}^GB${W - 80},${barH},${barH},B^FS`,
        `^FO40,${y + 25}^FB${W - 80},1,0,C^A0N,${nameDots},${nameDots}^FR^FD${name}^FS`,
    ];
    const head: string[] = [];
    if (classifier) {
        const classDots = courierPalletClassifierDots(opts.classifier);
        head.push(
            ...courierBar(40),
            `^FO0,${40 + barH + 25}^FB${W},1,0,C^A0N,28,28^FD${courierPalletClassifierCaption(opts.sku)}^FS`,
            `^FO0,${40 + barH + 65}^FB${W},1,0,C^A0N,${classDots},${classDots}^FD${classifier}^FS`,
        );
    } else {
        head.push(...courierBar(Math.round((580 - barH) / 2)));
    }
    // Barcode at 600, its value in small plain text right under it, then
    // the footer.
    return [
        "^XA", "^CI28", `^PW${W}`, "^LL1218", "^LH0,0", "^LS0",
        ...head,
        `^BY${by},3,200^FO${x},600^BCN,200,N,N,N^FD${zplText(opts.barcode)}^FS`,
        `^FO0,812^FB${W},1,0,C^A0N,28,28^FD${zplText(opts.barcode)}^FS`,
        `^FO0,880^FB${W},1,0,C^A0N,32,32^FD${footer}^FS`,
        "^XZ",
    ].join("\n");
}
